/**
 * Tiny framework-free i18n (English + 简体中文).
 *
 * No dependency, no build step: one dictionary keyed by string id, a `t()`
 * lookup with `{var}` interpolation, the active language persisted to
 * localStorage (auto-detected from the browser on first run), and a subscribe
 * hook so the DOM can re-render when the language changes.
 *
 * Discovery things/places and bond milestones live in the same table (keyed
 * by domain id) so the whole experience is localised from one place and the
 * completeness test covers everything.
 */

export type Lang = 'en' | 'zh';

const STORAGE_KEY = 'wwd.lang';

/** UI strings. Every key MUST exist in both locales (a test enforces this). */
const UI = {
  en: {
    'app.title': 'Walk with Datou',
    'status.trust': 'Trust',
    'console.leash': 'Leash',
    'console.pack': 'Backpack',
    'console.memories': 'Memories',
    'console.foundToday': 'Found today · {n}/{total}',
    'hint.firstSteps':
      'WASD / tap to walk · tap Datou to pet, hold to soothe · tap small plants to gather',
    'pack.title': 'Backpack',
    'pack.empty': 'Nothing gathered yet — tap twigs, pebbles and flowers as you walk.',
    'use.throw': 'Throw',
    'use.place': 'Place',
    'use.wear': 'Wear',
    'use.unwear': 'Take off',
    'place.hint': 'Tap the ground where you want it',
    'place.banner': 'Placing {thing} — tap the ground',
    'pickup.take': 'Pick up',
    'pickup.move': 'Move',
    'pickup.done': '{thing} is back in your pack',
    'bowl.filled': 'You set down something warm for Datou',
    'bowl.enjoyed': 'Datou takes his time with it',
    'bowl.reward': 'Clean bowl, quiet company',
    'gather.toast': 'Picked up {thing}',
    'fetch.return': 'Datou brought the stick back!',
    'react.sniff': 'Datou sniffs it over',
    'react.rustle': 'Datou noses through the leaves',
    'react.hop': 'Datou hops up for a look',
    'react.watch': 'Datou watches it for a while',
    'react.peer': 'Datou peers in close, ears forward',
    'memory.fetch': 'A good game of fetch',
    'thing.twig': 'a dry twig',
    'thing.pebble': 'a smooth pebble',
    'thing.berry': 'a berry sprig',
    'thing.flower': 'a wildflower',
    'thing.pinecone': 'a pinecone',
    'thing.reed': 'a cut reed',
    'thing.old-bolt': 'an old robot bolt',
    'thing.clay-lump': 'a soft lump of clay',
    'thing.stick': 'the fetch stick',
    'thing.cairn': 'a little cairn',
    'thing.garland': 'a flower garland',
    'thing.lantern': 'a small lantern',
    'thing.fence': 'a fence piece',
    'thing.plot': 'a garden plot',
    'thing.campfire': 'a campfire',
    'thing.shelter': "Datou's shelter",
    'thing.bundle': 'a twig bundle',
    'thing.stonepile': 'a stone pile',
    'thing.bench': 'a little bench',
    'thing.birdbath': 'a stone birdbath',
    'thing.windchime': 'a wind chime',
    'thing.archway': 'a garden archway',
    // --- Touchable world props: name + two seeded observations each (E1) ---
    'prop.tree': 'a leafy tree',
    'obs.tree.1': 'its leaves tick softly in the breeze',
    'obs.tree.2': 'the bark is warm on the sunny side',
    'prop.pine': 'a quiet pine',
    'obs.pine.1': 'it smells of resin and cold air',
    'obs.pine.2': 'needles carpet the ground beneath',
    'prop.bush': 'a round bush',
    'obs.bush.1': 'something rustled in there once',
    'obs.bush.2': 'the leaves grow dense and low',
    'prop.rock': 'a mossy boulder',
    'obs.rock.1': 'moss likes the north side',
    'obs.rock.2': 'it has sat here longer than the paths',
    'prop.stump': 'an old stump',
    'obs.stump.1': 'the rings count more years than you',
    'obs.stump.2': 'rain pools in the sawn top',
    'prop.reed': 'lakeside reeds',
    'obs.reed.1': 'they whisper when wind crosses the lake',
    'obs.reed.2': 'dragonflies like the tips',
    'prop.lamp': 'a trail lamp',
    'obs.lamp.1': 'it hums faintly at dusk',
    'obs.lamp.2': 'someone keeps the glass clean',
    'prop.bench': 'a worn bench',
    'obs.bench.1': 'the wood is smooth where people sit',
    'obs.bench.2': 'a good spot to watch the trail',
    'prop.signpost': 'a wooden signpost',
    'obs.signpost.1': 'the paint has faded to a guess',
    'obs.signpost.2': 'it points the long way round',
    'prop.jetty': 'the old jetty',
    'obs.jetty.1': 'the planks creak a slow hello',
    'obs.jetty.2': 'water laps underneath the boards',
    'prop.picnic': 'a picnic table',
    'obs.picnic.1': 'crumbs say it was used this week',
    'obs.picnic.2': 'initials are carved under the lip',
    'prop.bulletin': 'the bulletin board',
    'obs.bulletin.1': 'park notices, sun-bleached and curling',
    'obs.bulletin.2': 'someone pinned a drawing of a dog',
    'prop.mushroom': 'a ring toadstool',
    'obs.mushroom.1': 'it stands in a ring with its kin',
    'obs.mushroom.2': 'the cap is freckled and damp',
    'prop.fallen-log': 'a fallen log',
    'obs.fallen-log.1': 'the hollow end goes back further than light',
    'obs.fallen-log.2': 'moss has claimed the sunny side',
    'prop.fern': 'a fern clump',
    'obs.fern.1': 'older than trees, and acting like it',
    'obs.fern.2': 'the fronds unroll a little more each day',
    'prop.cattail': 'a cattail stand',
    'obs.cattail.1': 'velvet heads nodding over the water',
    'obs.cattail.2': 'red-winged shadows like to land here',
    'prop.anthill': 'an anthill',
    'obs.anthill.1': 'a whole city, and nobody is late',
    'obs.anthill.2': 'the tracks fan out to everywhere that matters',
    // --- Community dressing (E6) ---
    'dressing.line.1': 'someone made this with care',
    'dressing.line.2': 'worn smooth by use — the shape sticks with you',
    'dressing.taught': 'The making of it sticks — a silhouette inks into the notebook',
    // --- The great things: setpieces with staged beats (E2) ---
    'setpiece.hollow-oak.name': 'the Hollow Oak',
    'setpiece.hollow-oak.line.1': 'the dark inside breathes cool air',
    'setpiece.hollow-oak.line.2': 'something small keeps acorns in there',
    'memory.setpiece.hollow-oak': 'Braved the Hollow Oak together',
    'setpiece.old-pine.name': 'the Old Pine',
    'setpiece.old-pine.line.1': 'the tallest thing the woods ever grew',
    'setpiece.old-pine.line.2': 'its needles hiss like soft rain',
    'memory.setpiece.old-pine': 'Stood small under the Old Pine',
    'setpiece.swing-tree.name': 'the Swing Tree',
    'setpiece.swing-tree.line.1': 'the rope still holds, mostly',
    'setpiece.swing-tree.line.2': 'someone loved this branch for years',
    'memory.setpiece.swing-tree': 'The swing still works',
    'setpiece.willow-bend.name': 'the Willow Bend',
    'setpiece.willow-bend.line.1': 'its hair combs the water all day',
    'setpiece.willow-bend.line.2': 'the shade under it is ten years deep',
    'memory.setpiece.willow-bend': 'Sat in the willow’s green light',
    'setpiece.erratic.name': 'the Erratic',
    'setpiece.erratic.line.1': 'a glacier forgot this here',
    'setpiece.erratic.line.2': 'it is warmer than a stone should be',
    'memory.setpiece.erratic': 'Visited the stone a glacier forgot',
    'setpiece.jetty-end.name': 'the Jetty End',
    'setpiece.jetty-end.line.1': 'the last plank bounces a little',
    'setpiece.jetty-end.line.2': 'fish shadows cross under your feet',
    'memory.setpiece.jetty-end': 'Watched the water off the jetty end',
    'setpiece.star-circle.name': 'the Star Circle',
    'setpiece.star-circle.line.1': 'nine stones, laid by patient hands',
    'setpiece.star-circle.line.2': 'Datou swears it hums at noon',
    'memory.setpiece.star-circle': 'Listened to the Star Circle hum',
    'setpiece.kite-tree.name': 'the Kite Tree',
    'setpiece.kite-tree.line.1': 'a kite has lived up there for years',
    'setpiece.kite-tree.line.2': 'on windy days the tail still dances',
    'memory.setpiece.kite-tree': 'Cheered for the stuck kite',
    'setpiece.echo': 'Datou studies it — an idea inks into the notebook',
    // --- The Meadow Orchard (E4) ---
    'thing.apple': 'a windfall apple',
    'thing.pear': 'a garden pear',
    'thing.plum': 'a dusky plum',
    'thing.pumpkin': 'a small pumpkin',
    'thing.turnip': 'a round turnip',
    'thing.carrot': 'a feathery-top carrot',
    'orchard.tree.apple': 'an apple tree',
    'orchard.tree.pear': 'a pear tree',
    'orchard.tree.plum': 'a plum tree',
    'orchard.blossom': 'all blossom — fruit comes with summer',
    'orchard.bare': 'resting until spring',
    'orchard.spent': 'shaken empty today — more tomorrow',
    'orchard.shake': 'Datou trots over to nudge the trunk',
    'orchard.row.pumpkin': 'the pumpkin row',
    'orchard.row.turnip': 'the turnip row',
    'orchard.row.carrot': 'the carrot row',
    'orchard.rowSpent': 'picked over today — it regrows overnight',
    // --- The park's small animals (E5) ---
    'critter.bird': 'a meadow sparrow',
    'critter.bird.line': 'it was here first',
    'critter.butterfly': 'a blossom butterfly',
    'critter.butterfly.line': 'it works the flower drift all day',
    'critter.duck': 'a lake duck',
    'critter.duck.line': 'unbothered. paddling. in its lake.',
    'critter.cat': 'the trail cat',
    'critter.cat.line.1': 'it pretends not to see you',
    'critter.cat.line.2': 'it blinks at you, slowly — high praise',
    'critter.squirrel': 'the oak squirrel',
    'critter.squirrel.line': 'it files you under “probably fine”',
    'critter.dog': 'the neighbor dog',
    'critter.dog.line': 'a friendly face from along the trail',
    'critter.gift': 'The squirrel left something at the oak roots',
    'thing.acorn': 'a glossy acorn',
    'memory.want.critter.cat': 'The trail cat allowed a meeting',
    'memory.want.critter.duck': 'Datou had opinions about ducks',
    'memory.want.critter.dog': 'Played with the neighbor dog',
    'memory.want.critter.squirrel': 'Met the oak squirrel',
    'memory.want.critter.bird': 'Watched a sparrow about its day',
    'memory.want.critter.butterfly': 'Followed a butterfly for a while',
    // --- The morning note (E7) ---
    'morning.rain': 'Rain today — the garden drinks double',
    'morning.dog': 'The neighbor dog is around today',
    'morning.orchard': 'The orchard dropped fruit overnight',
    'morning.blossom': 'The orchard is all blossom this morning',
    'morning.winter': 'A quiet cold morning — the lamps stay warm',
    'craft.tier1': 'Components',
    'craft.tier2': 'Furnishings',
    'craft.tier3': 'Structures',
    'craft.componentHint': 'A building part — used in bigger makes below',
    'craft.resourceHint': 'A find from the walk — craft with it, or plant it in a garden plot',
    'use.component': 'Part',
    'place.cancelled': 'Back in your pack',
    'farm.planted': 'Planted {thing}',
    'farm.growing': 'It’s coming along…',
    'farm.harvest': 'Harvested {thing} ×{n}',
    'farm.empty': 'Gather a berry, flower or mushroom to plant here',
    'memory.harvest': 'Tended the garden together',
    'memory.forage': 'Datou kept bringing me things',
    'want.attention': 'Datou is looking at you…',
    'want.play': 'Datou wants to play!',
    'want.curious': 'Datou noticed something…',
    'memory.discovery': 'Found {thing} {place}',
    'memory.want.attention': 'A quiet moment together',
    'memory.want.play': 'A burst of play',
    'memory.want.curious': 'Followed its curiosity',
    'memory.comfort': 'A long soothing touch',
    'milestone.glance-back': 'Datou glances back to check on you now',
    'milestone.fetch': 'Datou trusts you enough to really play',
    'milestone.lie-at-feet': 'Datou lies down at your spot now',
    'milestone.initiate-explore': 'Datou leads the way sometimes',
    'milestone.signature': 'Datou has little ways of its own now',
    'thing.sprout': 'a tiny sprout',
    'thing.shiny': 'something shiny',
    'thing.feather': 'a soft feather',
    'thing.mushroom': 'a little mushroom',
    'thing.ladybug': 'a ladybug',
    'place.under-tree': 'under the big tree at home',
    'place.behind-rock': 'behind the boulder at home',
    'place.by-lamp': 'beside the little lamp',
    'place.old-pine': 'under the Old Pine',
    'place.woods-hollow': 'in a woods hollow',
    'place.mossy-clearing': 'in the mossy clearing',
    'place.rest-stop': 'by the trail rest stop',
    'place.trail-bend': 'at the trail bend',
    'place.lakeshore': 'on the lakeshore',
    'place.reed-bank': 'among the reeds',
    'place.lone-boulder': 'by the lone boulder',
    'place.high-meadow': 'in the high meadow',
    'place.jetty-end': 'at the end of the jetty',
    'place.ruin-stones': 'among the ruin stones',
    'memories.title': 'Memories',
    'memories.empty': 'No memories yet — spend a little time together.',
    'mood.happy': 'happy',
    'mood.calm': 'calm',
    'mood.curious': 'curious',
    'mood.tired': 'tired',
    // --- Workshop item-name composition (BUILDING_SYSTEM §2.3) -------------
    // name = size adjective + material + form, assembled by name.order.
    'name.order': '{size} {material} {form}',
    'name.sizeAdj.S': 'little',
    'name.sizeAdj.L': 'tall',
    'rarity.common': 'Common',
    'rarity.uncommon': 'Uncommon',
    'rarity.rare': 'Rare',
    'rarity.epic': 'Epic',
    'rarity.legendary': 'Legendary',
    // --- The Workshop (BUILDING_SYSTEM §6) -------------------------------
    'console.workshop': 'Workshop',
    'workshop.title': 'Workshop',
    'workshop.tab.bench': 'Bench',
    'workshop.tab.tree': 'Tree',
    'workshop.tab.notebook': 'Notebook',
    'workshop.benchHint': 'Drag a material across the bench. Right-click a cell to take one back.',
    'workshop.noMaterials':
      'Gather twigs, stones and flowers on your walk — then make things here.',
    'workshop.result': 'Result',
    'workshop.resultEmpty': 'Arrange materials on the bench to see what they become.',
    'workshop.make': 'Make',
    'workshop.curio': 'a little curio',
    'workshop.curios': 'Curios · {n}',
    'workshop.made': 'We figured out {thing}',
    'workshop.toPack': '{thing} — it’s in your pack',
    'workshop.foundOf': '{n} of {total} found',
    'workshop.branchLocked': 'Nothing discovered here yet',
    'workshop.notebookEmpty': 'Nothing banked yet — walk with Datou and watch for ideas.',
    'workshop.needNearBench': 'Stand by the workbench to open it',
    'workshop.inspired': 'Datou had an idea — it’s in your notebook',
    'workshop.pinForage': 'Send Datou to fetch this',
    'forage.pinned': 'Datou trots off to find {thing}',
    'forage.delivered': 'Datou brought back {n}',
    'forage.cantFind': 'Datou can’t forage that one',
    'forage.stop': 'Call Datou back',
    'console.fetch': 'Fetch',
    'forage.title': 'Send Datou to fetch',
    'forage.hint': 'Pick something for Datou to find and bring back.',
    'forage.none': 'Nothing to fetch nearby right now — walk a little further.',
    'forage.working': 'Datou is off fetching {thing}…',
    'forage.callBack': 'Call Datou back',
    'forage.viaForage': 'Datou will forage for it',
    'forage.viaNode': 'Datou will work a source with the tool',
    'forage.blocked': 'Needs the right tool',
    'forage.needTool': 'Make the tool first',
    'forage.somewhere': 'something',
    'node.name.great-tree': 'a Great Tree',
    'node.name.old-boulder': 'an Old Boulder',
    'node.name.clay-seam': 'the clay seam',
    'node.name.flint-lode': 'the flint lode',
    'node.name.bolt-cache': 'the bolt cache',
    'node.line.great-tree': 'a trunk too wide to hug — an axe could win good logs',
    'node.line.old-boulder': 'a pickaxe could chip good stone from it',
    'node.line.clay-seam': 'damp clay hides in the wet bank',
    'node.line.flint-lode': 'flint glints in the cracked seam',
    'node.line.bolt-cache': 'old machine bones, bolts still in them',
    'node.name.reed-bed': 'a reed bed',
    'node.line.reed-bed': 'shears could cut a clean armful here',
    'node.name.shell-bank': 'a shell bank',
    'node.line.shell-bank': 'the sand gives up shells to a gentle scoop',
    'node.name.driftwood': 'a driftwood drift',
    'node.line.driftwood': 'silvered logs the lake left — easy axe work',
    'node.needTool': 'Datou paws at it — you’ll need the right tool',
    'node.refused': 'Datou is tired and sits this one out',
    'node.spent': 'Worked out for now — it’ll come back',
    'coffer.opened':
      'Ten small home blueprints, the fetch stick, two tools, and enough material to begin. Open the Workshop.',
    'coffer.context': 'from the little chest near home',
    'memory.coffer': 'Found a little chest of ideas',
    // --- Community landmarks (landmark plan §7–§9) -------------------------
    'landmark.repair-commons.arrive': 'The Trail Repair Commons — someone keeps this trail going',
    'landmark.pump-garden.arrive': 'The Reedwater Pump Garden — a handmade wetland garden',
    'landmark.relay-camp.arrive': 'The Old Pine Relay Camp — volunteers ran a park relay here',
    'landmark.ruin-stones.arrive':
      'The Ruin Stones — the park’s first shelter. The fragment’s mark is everywhere.',
    'landmark.watch-knoll.arrive': 'The Watchers’ Knoll — bird boards, long grass, a wide sky',
    'landmark.meadow-orchard.arrive':
      'The Meadow Orchard — planted rows, sweet with coming blossom',
    'landmark.inspect.pennant-mast.name': 'Patchwork pennant mast',
    'landmark.inspect.pennant-mast.desc':
      'A tall salvaged marker that makes the volunteer repair stop visible from the trail.',
    'landmark.inspect.message-chime.name': 'Trail message chime',
    'landmark.inspect.message-chime.desc':
      'Crews left this chime for walkers. Its clear ring means the Commons is being cared for.',
    'landmark.inspect.pump-wheel.name': 'Handmade pump wheel',
    'landmark.inspect.pump-wheel.desc':
      'The garden’s timber pump lifts water into the channels and floating beds.',
    'landmark.inspect.floating-planter.name': 'Floating planter',
    'landmark.inspect.floating-planter.desc':
      'A light planting box that rises with the restored water loop.',
    'landmark.inspect.relay-mast.name': 'Old park relay',
    'landmark.inspect.relay-mast.desc':
      'Volunteers used this mast to pass small reports between distant corners of the park.',
    'landmark.inspect.signal-vane.name': 'Signal vane',
    'landmark.inspect.signal-vane.desc':
      'One of two tuning vanes. Their bearings wake the relay when both sit true.',
    'landmark.inspect.marked-stone.name': 'Ring-marked shelter stone',
    'landmark.inspect.marked-stone.desc':
      'The carved mark belongs to the park’s first shelter and its old network of keepers.',
    'landmark.inspect.spotter-tube.name': 'Watchers’ spotter tube',
    'landmark.inspect.spotter-tube.desc':
      'A simple field scope set here for watching swifts cross the open sky.',
    'landmark.inspect.orchard-sapling.name': 'Young orchard tree',
    'landmark.inspect.orchard-sapling.desc':
      'One tree in the planted row has slipped its stake and needs setting upright.',
    'landmark.inspect.stepping-stones.name': 'Stream stepping stones',
    'landmark.inspect.stepping-stones.desc':
      'Flat stones placed by hand to keep the old crossing dry and steady.',
    'landmark.inspect.willow-tree.name': 'Sheltering willow',
    'landmark.inspect.willow-tree.desc':
      'Its hanging branches make a quiet green room around the stream-side seat.',
    'landmark.inspect.marsh-pool.name': 'Frog shallows pool',
    'landmark.inspect.marsh-pool.desc':
      'A still, reed-ringed pool kept open for frogs, insects, and small wetland life.',
    'landmark.inspect.lantern-avenue.name': 'Waiting lantern avenue',
    'landmark.inspect.lantern-avenue.desc':
      'A short paved walk whose handmade lamps guide visitors toward the lakeshore.',
    'landmark.inspect.old-boardwalk.name': 'Old marsh boardwalk',
    'landmark.inspect.old-boardwalk.desc':
      'Weathered planks carry the path across soft marsh ground without disturbing it.',
    'landmark.inspect.driftwood-arch.name': 'Driftwood arch',
    'landmark.inspect.driftwood-arch.desc':
      'Beach-combers lashed pale shore wood into a marker that frames the southern sky.',
    'landmark.inspect.kite-post.name': 'Community kite post',
    'landmark.inspect.kite-post.desc':
      'Lines and spools gather here so the open field can be shared by many flyers.',
    'landmark.inspect.meadow-gate.name': 'Old meadow gate',
    'landmark.inspect.meadow-gate.desc':
      'This handmade gate marks the eastern edge while keeping the path welcoming.',
    'landmark.inspect.beacon-basket.name': 'Keeper’s beacon basket',
    'landmark.inspect.beacon-basket.desc':
      'A small iron fire basket once used to answer signals across the high ground.',
    'landmark.inspect.star-stone.name': 'Drilled star stone',
    'landmark.inspect.star-stone.desc':
      'One of five seat-stones whose tiny drilled stars echo the night sky above.',
    'landmark.inspect.hollow-oak.name': 'Exchange oak',
    'landmark.inspect.hollow-oak.desc':
      'The old hollow holds a community shelf: leave something useful, take what you need.',
    'landmark.inspect.swing-board.name': 'Old rope swing',
    'landmark.inspect.swing-board.desc':
      'A simple board and two ropes, checked and kept sound by people passing through.',
    'landmark.inspect.hive-stand.name': 'Meadow hive stand',
    'landmark.inspect.hive-stand.desc':
      'Raised hives sit level among the flowers, with clear lanes for the bees.',
    'landmark.inspect.giant-fern.name': 'Giant hollow fern',
    'landmark.inspect.giant-fern.desc':
      'Broad fronds hold the damp shade and give this woodland hollow its deep green character.',
    'landmark.inspect.quarry-face.name': 'Old quarry face',
    'landmark.inspect.quarry-face.desc':
      'These cut marks show where much of the park’s stone was shaped and carried away.',
    'landmark.ruins.trace': 'Datou sets his paw beside the mark; you rub it through together…',
    'landmark.ruins.done':
      'Under the chalk, the mark answers: a sketch of the old network — a watchers’ rise west of home, planted rows in the high meadow.',
    'landmark.ruins.again.0': 'The chalk mark catches the light.',
    'landmark.ruins.again.1': 'A moth circles the traced ring, patient as stone.',
    'landmark.knoll.steady': 'Datou braces the tripod while you level the tube…',
    'landmark.knoll.done':
      'The tube settles true — and the swifts come wheeling back over the rise.',
    'landmark.knoll.again.0': 'Swifts wheel over the knoll.',
    'landmark.knoll.again.1': 'The wind combs the silver grass.',
    'landmark.orchard.stake': 'Datou tamps the soil while you draw the sapling upright…',
    'landmark.orchard.done':
      'The sapling stands. By the time you step back, the row is full of blossom.',
    'landmark.orchard.again.0': 'Petals drift between the rows.',
    'landmark.orchard.again.1': 'The butterflies are busy in the clover.',
    // --- Light landmarks (map-fill wave) -----------------------------------
    'landmark.lightAgain.0': 'The place keeps well.',
    'landmark.lightAgain.1': 'Datou gives it a satisfied once-over.',
    'landmark.lightAgain.2': 'Someone has visited since you — things sit a little differently.',
    'landmark.stepping-stones.arrive': 'The Stepping Stones — the stream crossing, worn smooth',
    'landmark.stepping-stones.tend': 'Datou steadies each stone while you settle them true…',
    'landmark.stepping-stones.done':
      'The crossing sits firm. Downstream, a willow leans over the bend.',
    'landmark.willow-bend.arrive': 'The Willow Bend — a green room over the stream',
    'landmark.willow-bend.tend': 'You part the curtain fronds; Datou clears the seat beneath…',
    'landmark.willow-bend.done':
      'The bench under the willow is clear again. The stream goes on toward the marsh.',
    'landmark.frog-shallows.arrive': 'The Frog Shallows — pools, cattails, and a patient chorus',
    'landmark.frog-shallows.tend': 'Datou holds very still while you skim the pools clear…',
    'landmark.frog-shallows.done': 'The pools settle to glass. One frog approves, loudly.',
    'landmark.lantern-walk.arrive': 'The Lantern Walk — a paved little avenue, lamps waiting',
    'landmark.lantern-walk.tend': 'Datou steadies each post while you straighten the lamps…',
    'landmark.lantern-walk.done':
      'The avenue stands trim. Its paving runs on toward the shore boardwalk.',
    'landmark.boardwalk-rest.arrive': 'The Old Boardwalk — planks over the marsh, one gap to mind',
    'landmark.boardwalk-rest.tend': 'Datou braces the runner while you seat the loose planks…',
    'landmark.boardwalk-rest.done':
      'The walk holds underfoot. South along the shore, driftwood arches stand.',
    'landmark.driftwood-beach.arrive': 'The Driftwood Beach — bleached arches on the south shore',
    'landmark.driftwood-beach.tend': 'You re-lash the leaning arch; Datou leans where told…',
    'landmark.driftwood-beach.done':
      'The arches stand against the sky. The beach-combers would be pleased.',
    'landmark.kite-field.arrive': 'The Kite Field — a mown strip and two kites pinned to the sky',
    'landmark.kite-field.tend': 'Datou holds the spool while you take in the slack…',
    'landmark.kite-field.done':
      'The lines sit taut and the kites ride steady. East, an old gate breaks the fence line.',
    'landmark.gate-arch.arrive': 'The Meadow Gate — an arch standing ajar on the east edge',
    'landmark.gate-arch.tend': 'Datou noses the gate while you ease the old hinge…',
    'landmark.gate-arch.done':
      'The gate swings true and latches soft. The fence line walks north toward a beacon.',
    'landmark.beacon-rise.arrive': 'The Beacon Rise — an iron fire-basket against the sky',
    'landmark.beacon-rise.tend': 'You lay the kindling; Datou watches the basket like a hawk…',
    'landmark.beacon-rise.done':
      'A small kept flame takes. From here the cairn line runs to the corner stones.',
    'landmark.star-circle.arrive': 'The Star Circle — five seat-stones facing the night sky',
    'landmark.star-circle.tend': 'You brush off each seat-stone; Datou tries every one…',
    'landmark.star-circle.done':
      'The circle is swept and ready for dark. The drilled stars match the real ones.',
    'landmark.fern-hollow.arrive': 'The Fern Hollow — a deep green dell under the pines',
    'landmark.fern-hollow.tend': 'You lift the flattened fronds; Datou pads the soft floor flat…',
    'landmark.fern-hollow.done':
      'The hollow breathes again, green on green. Cart ruts lead away west.',
    'landmark.quarry-scar.arrive': 'The Old Quarry — cut faces and a half-freed block',
    'landmark.quarry-scar.tend': 'Datou braces the leaning block while you chock it safe…',
    'landmark.quarry-scar.done':
      'The block rests safe on its chocks. The park’s stone all began here.',
    'landmark.hollow-oak.arrive': 'The Hollow Oak — leave a thing, take a thing',
    'landmark.hollow-oak.tend': 'You tidy the exchange shelf; Datou inspects every item…',
    'landmark.hollow-oak.done':
      'The shelf is set fair. Footprints lead on toward a swing under a big limb.',
    'landmark.swing-tree.arrive': 'The Swing Tree — a board swing hanging plumb-still',
    'landmark.swing-tree.tend': 'Datou tests the board; you check both ropes for wear…',
    'landmark.swing-tree.done': 'The swing holds sound. Datou rides it exactly once, with dignity.',
    'landmark.bee-meadow.arrive': 'The Bee Meadow — hives in a drift of flowers',
    'landmark.bee-meadow.tend': 'You level the hive stand; Datou keeps a respectful distance…',
    'landmark.bee-meadow.done':
      'The hives sit level and the lanes hum. The flower drift runs on toward the orchard.',
    'landmark.commons.chimeNeedsPart':
      'The chime is tangled and its clapper is gone. A twig would do.',
    'landmark.commons.chimeStart': 'Datou braces the post while you untangle the chime…',
    'landmark.commons.chimeDone': 'The chime rings clear — the mast lamp warms up.',
    'landmark.commons.again.0':
      'The chime rings clear as you pass — the stop keeps its small welcome.',
    'landmark.commons.again.1': 'A breeze worries the pennants; the mast lamp holds its warmth.',
    'landmark.commons.again.2': 'Someone has pinned a fresh thank-you note to the board.',
    'landmark.commons.chimeDonated':
      'Your chime joins the rest stop — it will greet whoever passes.',
    'landmark.capsule.found':
      'Beneath the Old Pine: a volunteers’ time capsule — a tiny keepsake for the Notebook.',
    'coffer.repair-commons.opened':
      'A chime supply box — a full blueprint, and the makings for one. Chimes mark friendly rest stops.',
    'coffer.repair-commons.context': 'from the supply box at the Repair Commons',
    'coffer.pump-garden.opened':
      'A planter tool chest — a full blueprint, and the makings for one. A water-stained sketch shows an empty socket in the garden.',
    'coffer.pump-garden.context': 'from the tool chest at the Pump Garden',
    'coffer.relay-camp.opened':
      'A relay field case — a wayfinder blueprint and parts. A fragment inside bears an unfamiliar mark.',
    'coffer.relay-camp.context': 'from the field case at the Relay Camp',
    'coffer.ruin-stones.opened':
      'An old waxed satchel — a lantern blueprint, and the makings for one. The shelter kept its light.',
    'coffer.ruin-stones.context': 'from the satchel at the Ruin Stones',
    'coffer.watch-knoll.opened':
      'The watchers’ kit chest — a field-glass blueprint and parts. See what they saw.',
    'coffer.watch-knoll.context': 'from the kit chest on the Watchers’ Knoll',
    'coffer.meadow-orchard.opened':
      'The planters’ seed chest — a bug-hotel blueprint and the makings. The pollinators need housing.',
    'coffer.meadow-orchard.context': 'from the seed chest at the Meadow Orchard',
    'coffer.stepping-stones.opened':
      'A ford-keeper’s box — a path-tile blueprint and the stone to lay one.',
    'coffer.stepping-stones.context': 'from the box at the Stepping Stones',
    'coffer.willow-bend.opened': 'A keepsake tin — a garland blueprint and a handful of flowers.',
    'coffer.willow-bend.context': 'from the tin at the Willow Bend',
    'coffer.frog-shallows.opened':
      'A tarred chest — a raft blueprint and the makings. Someone meant to float the marsh.',
    'coffer.frog-shallows.context': 'from the chest at the Frog Shallows',
    'coffer.lantern-walk.opened': 'A lamplighter’s box — a lamp blueprint and its fittings.',
    'coffer.lantern-walk.context': 'from the box on the Lantern Walk',
    'coffer.boardwalk-rest.opened':
      'A walk-mender’s chest — a bridge-plank blueprint and the timber for one.',
    'coffer.boardwalk-rest.context': 'from the chest at the Old Boardwalk',
    'coffer.driftwood-beach.opened':
      'A comber’s cache — an archway blueprint and the driftwood-build makings.',
    'coffer.driftwood-beach.context': 'from the cache at the Driftwood Beach',
    'coffer.kite-field.opened': 'A flyer’s box — a wind-vane blueprint and parts. Know your wind.',
    'coffer.kite-field.context': 'from the box at the Kite Field',
    'coffer.gate-arch.opened': 'A gate-warden’s chest — a gate blueprint and the timber for one.',
    'coffer.gate-arch.context': 'from the chest at the Meadow Gate',
    'coffer.beacon-rise.opened':
      'A signal-keeper’s chest — a lookout-perch blueprint and its timber.',
    'coffer.beacon-rise.context': 'from the chest on the Beacon Rise',
    'coffer.star-circle.opened': 'A stargazer’s tin — a mobile blueprint and feathers to hang.',
    'coffer.star-circle.context': 'from the tin at the Star Circle',
    'coffer.fern-hollow.opened': 'A forester’s pouch — a brush blueprint and its soft makings.',
    'coffer.fern-hollow.context': 'from the pouch in the Fern Hollow',
    'coffer.quarry-scar.opened': 'A mason’s chest — a well blueprint and a heavy count of stone.',
    'coffer.quarry-scar.context': 'from the chest at the Old Quarry',
    'coffer.hollow-oak.opened': 'An exchange parcel — a cache-box blueprint, latch included.',
    'coffer.hollow-oak.context': 'from the parcel in the Hollow Oak',
    'coffer.swing-tree.opened': 'A rigger’s tin — a cord blueprint and reed to twist.',
    'coffer.swing-tree.context': 'from the tin at the Swing Tree',
    'coffer.bee-meadow.opened':
      'A keeper’s box — a vessel blueprint and smooth stones. For the honey, one day.',
    'coffer.bee-meadow.context': 'from the box at the Bee Meadow',
    'landmark.garden.channelTurned': 'The channel piece swings back into line.',
    'landmark.garden.channelSet': 'This piece already sits true.',
    'landmark.garden.pumping':
      'The loop is whole — Datou settles at the pump and works it in slow beats…',
    'landmark.garden.done': 'Water travels the loop. The paper plants lift and take color.',
    'landmark.garden.again.0': 'The garden burbles along quietly.',
    'landmark.garden.again.1': 'A dragonfly rests on the pump wheel between beats.',
    'landmark.garden.again.2':
      'The floating planters have drifted into a new little constellation.',
    'landmark.garden.socketWaits': 'An empty planter socket — it waits for a made planter.',
    'landmark.garden.socketFilled': 'The planter settles into the garden. It belongs here.',
    'landmark.camp.vaneTurn': 'The vane clicks to a new bearing.',
    'landmark.camp.vaneGood': 'Datou’s ears lift — that one rings true.',
    'landmark.camp.aligned': 'Both vanes sit true — Datou goes very still, then turns to the mast…',
    'landmark.camp.done':
      'The mast wakes with a slow amber breath. Far across the meadow, something answers from the ruin stones.',
    'landmark.camp.snippet.0': 'The relay murmurs: …shelter at the lake garden, water restored…',
    'landmark.camp.snippet.1':
      'The relay murmurs: …two lost park robots found their way home this season…',
    'landmark.camp.snippet.2':
      'The relay murmurs: …signal faint from the far corner stones — still listening…',
    'memory.landmark.repair-commons': 'We fixed the trail chime together',
    'memory.landmark.pump-garden': 'We restarted the water garden together',
    'memory.landmark.relay-camp': 'We tuned the old relay — and something answered',
    'memory.landmark.coffer.repair-commons': 'Datou pawed open the chime supply box',
    'memory.landmark.coffer.pump-garden': 'Datou braced the warped lid of the tool chest',
    'memory.landmark.coffer.relay-camp': 'Datou felt a sleeping signal in the field case',
    'memory.landmark.ruin-stones': 'We traced the first shelter’s mark',
    'memory.landmark.watch-knoll': 'We brought the swifts back to the knoll',
    'memory.landmark.meadow-orchard': 'We set the orchard sapling right',
    'memory.landmark.coffer.ruin-stones': 'Datou nosed the old satchel from under the lintel',
    'memory.landmark.coffer.watch-knoll': 'Datou found the watchers’ kit chest in the grass',
    'memory.landmark.coffer.meadow-orchard': 'Datou pawed open the planters’ seed chest',
    'memory.landmark.stepping-stones': 'We settled the stream crossing together',
    'memory.landmark.willow-bend': 'We cleared the seat under the willow',
    'memory.landmark.frog-shallows': 'We skimmed the frog pools to glass',
    'memory.landmark.lantern-walk': 'We straightened the lantern avenue',
    'memory.landmark.boardwalk-rest': 'We seated the boardwalk planks',
    'memory.landmark.driftwood-beach': 'We re-lashed the driftwood arches',
    'memory.landmark.kite-field': 'We took the slack out of the kite lines',
    'memory.landmark.gate-arch': 'We eased the meadow gate true',
    'memory.landmark.beacon-rise': 'We lit a small kept flame on the rise',
    'memory.landmark.star-circle': 'We swept the star circle for the night',
    'memory.landmark.fern-hollow': 'We let the fern hollow breathe again',
    'memory.landmark.quarry-scar': 'We chocked the quarry block safe',
    'memory.landmark.hollow-oak': 'We set the exchange shelf fair',
    'memory.landmark.swing-tree': 'We made the old swing sound again',
    'memory.landmark.bee-meadow': 'We levelled the hives in the flower drift',
    'memory.landmark.coffer.stepping-stones': 'Datou nosed out the ford-keeper’s box',
    'memory.landmark.coffer.willow-bend': 'Datou found the keepsake tin in the roots',
    'memory.landmark.coffer.frog-shallows': 'Datou dragged the tarred chest from the sedge',
    'memory.landmark.coffer.lantern-walk': 'Datou found the lamplighter’s box',
    'memory.landmark.coffer.boardwalk-rest': 'Datou pried at the walk-mender’s chest',
    'memory.landmark.coffer.driftwood-beach': 'Datou dug the comber’s cache from the sand',
    'memory.landmark.coffer.kite-field': 'Datou turned up the flyer’s box in the grass',
    'memory.landmark.coffer.gate-arch': 'Datou pawed the gate-warden’s chest open',
    'memory.landmark.coffer.beacon-rise': 'Datou found the signal-keeper’s chest',
    'memory.landmark.coffer.star-circle': 'Datou uncovered the stargazer’s tin',
    'memory.landmark.coffer.fern-hollow': 'Datou sniffed out the forester’s pouch',
    'memory.landmark.coffer.quarry-scar': 'Datou worried the mason’s chest free',
    'memory.landmark.coffer.hollow-oak': 'Datou reached the parcel deep in the hollow',
    'memory.landmark.coffer.swing-tree': 'Datou found the rigger’s tin by the roots',
    'memory.landmark.coffer.bee-meadow': 'Datou opened the keeper’s box, very carefully',
    'memory.landmark.socket': 'Our planter joined the garden',
    'memory.landmark.chime-donated': 'Our chime now greets the trail',
    'landmark.toy.repair-commons.0': 'Datou nudges the patch ball across the yard, very pleased.',
    'landmark.toy.repair-commons.1': 'The patch ball rolls; Datou pounces with great ceremony.',
    'landmark.toy.pump-garden.0':
      'Datou drops the reed ring in the shallows and fishes it right back out.',
    'landmark.toy.pump-garden.1': 'A careful splash — Datou parades the dripping ring around.',
    'landmark.toy.relay-camp.0': 'Datou boops the bell. It answers. He is deeply satisfied.',
    'landmark.toy.relay-camp.1': 'One soft clang — Datou’s ears swivel to catch the echo.',
    'landmark.toy.ruin-stones.0': 'Datou rolls the stone orb in a slow, ceremonial circle.',
    'landmark.toy.ruin-stones.1': 'The orb wobbles to a stop; Datou inspects it gravely.',
    'landmark.toy.watch-knoll.0': 'Datou leaps for the feather wand, misses, pretends he didn’t.',
    'landmark.toy.watch-knoll.1': 'The wand springs back; Datou crouches for another go.',
    'landmark.toy.meadow-orchard.0': 'Datou shakes the seed rattle and startles himself, twice.',
    'landmark.toy.meadow-orchard.1': 'A solemn rattle solo, performed for the butterflies.',
    'memory.landmark.capsule': 'We found the volunteers’ time capsule',
    'group.wood': 'wood',
    'group.stone': 'stone',
    'group.plant': 'plant',
    'group.found': 'found',
    'workshop.build': 'Build',
    'workshop.needMore': 'Need more',
    'workshop.recipeUnknown': 'Discover this on the bench',
    'workshop.askDatou': 'Ask Datou to fetch the rest',
    'workshop.god': 'God mode',
    'workshop.godHint': 'Reveal the whole tree and make anything for free',
    'workshop.godCreate': 'Create (free)',
    'tool.equipped': 'Datou clips the tool onto its arm',
    'workshop.family.component': 'Components',
    'workshop.family.furnishing': 'Furnishings',
    'workshop.family.structure': 'Structures',
    'workshop.family.datou': 'For Datou',
    'workshop.family.keepsake': 'Keepsakes',
    'workshop.family.tool': 'Tools',
    'thing.workbench': 'a workbench',
    // Materials (§2.1) — the vocabulary reused across the whole item space.
    'material.twig': 'twig',
    'material.bark': 'bark',
    'material.plank': 'plank',
    'material.driftwood': 'driftwood',
    'material.pine-branch': 'pine',
    'material.log': 'log',
    'material.pebble': 'pebble',
    'material.flat-stone': 'flagstone',
    'material.stone-block': 'stone',
    'material.clay-lump': 'clay',
    'material.flint': 'flint',
    'material.grass-wisp': 'grass',
    'material.reed': 'reed',
    'material.flower': 'flower',
    'material.berry': 'berry',
    'material.mushroom': 'mushroom',
    'material.pinecone': 'pinecone',
    'material.acorn': 'acorn',
    'material.apple': 'apple',
    'material.pear': 'pear',
    'material.plum': 'plum',
    'material.pumpkin': 'pumpkin',
    'material.turnip': 'turnip',
    'material.carrot': 'carrot',
    'material.feather': 'feather',
    'material.shell': 'shell',
    'material.old-bolt': 'bolt',
    // Forms (§2.2) — the F-axis vocabulary.
    'form.bundle': 'bundle',
    'form.pile': 'pile',
    'form.cord': 'cord',
    'form.beam': 'beam',
    'form.post': 'post',
    'form.panel': 'panel',
    'form.ring': 'ring',
    'form.vessel': 'vessel',
    'form.wheel': 'wheel',
    'form.block': 'block',
    'form.fence': 'fence',
    'form.gate': 'gate',
    'form.bench': 'bench',
    'form.table': 'table',
    'form.stool': 'stool',
    'form.lamp': 'lamp',
    'form.lantern': 'lantern',
    'form.planter': 'planter',
    'form.trellis': 'trellis',
    'form.birdbath': 'birdbath',
    'form.chime': 'chime',
    'form.mobile': 'mobile',
    'form.path-tile': 'path tile',
    'form.sign': 'sign',
    'form.mat': 'mat',
    'form.basket': 'basket',
    'form.cache-box': 'cache box',
    'form.drinking-bowl': 'drinking bowl',
    'form.bug-hotel': 'bug hotel',
    'form.sprout-pot': 'sprout pot',
    'form.mailbox': 'mailbox',
    'form.mushroom-lamp': 'mushroom lamp',
    'form.pet-bed': 'pet bed',
    'form.food-bowl': 'food bowl',
    'form.garden-lantern': 'garden lantern',
    'form.seed-chest': 'seed chest',
    'form.repair-toy': 'repair toy',
    'form.steam-rest': 'steam rest',
    'form.nose-puzzle-drawer': 'nose puzzle',
    'form.paw-rinse-step': 'paw rinse',
    'form.moonwater-lamp': 'moonwater lamp',
    'form.bird-nesting-fiber-frame': 'nesting frame',
    'form.weather-log-wheel': 'weather wheel',
    'form.spin-choice-wheel': 'choice wheel',
    'form.shared-snack-tin': 'snack tin',
    // --- Expansion: 492 form names (ITEM_DESIGN_EXPANSION_500) ---
    'form.tea-leaf-rinser': 'tea leaf rinser',
    'form.cup-warming-tile': 'cup warming tile',
    'form.travel-tea-caddy': 'travel tea caddy',
    'form.pour-over-stand': 'pour-over stand',
    'form.herb-steeping-basket': 'herb steeping basket',
    'form.kettle-nest': 'kettle nest trivet',
    'form.dew-collector-cup': 'dew collector cup',
    'form.tea-timer-float': 'tea timer float',
    'form.warming-flask-sleeve': 'warming flask sleeve',
    'form.shared-sip-tray': 'shared sip tray',
    'form.tea-brick-press': 'tea brick press',
    'form.spice-steam-pot': 'spice steam pot',
    'form.cup-rinsing-fountain': 'cup rinsing fountain',
    'form.tea-cloth-rack': 'tea cloth rack',
    'form.honey-drip-stand': 'honey drip stand',
    'form.warm-milk-pan': 'warm milk pan',
    'form.tea-scoop-rest': 'tea scoop rest',
    'form.herb-drying-dome': 'herb drying dome',
    'form.picnic-brew-box': 'picnic brew box',
    'form.charcoal-tea-brazier': 'charcoal tea brazier',
    'form.cupboard-tea-drawer': 'cupboard tea drawer',
    'form.tea-water-filter': 'tea water filter',
    'form.steam-chime-kettle': 'steam chime kettle',
    'form.tea-sharing-stool': 'tea sharing stool',
    'form.vegetable-washing-cradle': 'vegetable washing cradle',
    'form.root-scrubbing-board': 'root scrubbing board',
    'form.herb-chopping-trough': 'herb chopping trough',
    'form.dumpling-folding-tray': 'dumpling folding tray',
    'form.rice-rinsing-basket': 'rice rinsing basket',
    'form.soup-tasting-stand': 'soup tasting stand',
    'form.broth-cooling-rack': 'broth cooling rack',
    'form.bread-proofing-basket': 'bread proofing basket',
    'form.hand-mill-table': 'hand mill table',
    'form.noodle-drying-frame': 'noodle drying frame',
    'form.pickle-weight-press': 'pickle weight press',
    'form.fruit-slicing-guide': 'fruit slicing guide',
    'form.seed-roasting-pan': 'seed roasting pan',
    'form.meal-cover-cloche': 'meal cover cloche',
    'form.crumb-catching-board': 'crumb catching board',
    'form.spice-drawer-wheel': 'spice drawer wheel',
    'form.pan-lid-tree': 'pan lid tree',
    'form.utensil-rinse-pot': 'utensil rinse pot',
    'form.lunch-wrapping-press': 'lunch wrapping press',
    'form.compost-scrap-caddy': 'compost scrap caddy',
    'form.cooling-window-shelf': 'cooling window shelf',
    'form.tabletop-pot-warmer': 'tabletop pot warmer',
    'form.portion-scoop-board': 'portion scoop board',
    'form.pantry-inventory-rail': 'pantry inventory rail',
    'form.window-nap-sling': 'window nap sling',
    'form.floor-reading-nest': 'floor reading nest',
    'form.boot-warming-mat': 'boot warming mat',
    'form.lap-blanket-basket': 'lap blanket basket',
    'form.pillow-airing-frame': 'pillow airing rack',
    'form.quiet-corner-screen': 'quiet corner screen',
    'form.weighted-comfort-pad': 'weighted comfort pad',
    'form.backrest-roll': 'backrest bolster cradle',
    'form.cooling-reed-mat': 'cooling reed mat',
    'form.winter-foot-pocket': 'winter foot muff',
    'form.folding-nap-canopy': 'folding nap canopy',
    'form.headrest-drawer': 'keepsake headrest pillow',
    'form.blanket-ladder': 'blanket ladder',
    'form.cushion-mending-hoop': 'cushion mending hoop',
    'form.rest-bell-pillow': 'rest bell pillow',
    'form.story-quilt-frame': 'story quilt frame',
    'form.porch-foot-hammock': 'porch foot hammock',
    'form.travel-bed-roll': 'travel bed roll',
    'form.warm-stone-pocket': 'warm stone pocket',
    'form.rain-cloak-dryer': 'rain cloak dryer',
    'form.soft-step-runner': 'soft step runner',
    'form.cushion-stack-cart': 'cushion stack cart',
    'form.canopy-tieback-tree': 'canopy tieback stand',
    'form.bedside-slipper-cradle': 'bedside slipper cradle',
    'form.sun-warmed-lounger': 'sun warmed lounger',
    'form.field-journal-cradle': 'field journal cradle',
    'form.map-folding-board': 'map folding board',
    'form.leaf-press-book': 'leaf press book',
    'form.ink-drying-carousel': 'ink drying carousel',
    'form.pencil-shaving-cup': 'pencil shaving cup',
    'form.bookmark-weaving-frame': 'bookmark weaving loom',
    'form.postcard-drying-line': 'postcard drying line',
    'form.story-scroll-box': 'story scroll box',
    'form.nature-rubbing-board': 'nature rubbing board',
    'form.portable-writing-knee': 'portable lap desk',
    'form.paper-flower-cutter': 'paper flower cutter',
    'form.note-sorting-tree': 'note sorting tree',
    'form.stamp-carving-block': 'stamp carving block',
    'form.folding-letter-screen': 'folding letter screen',
    'form.charcoal-sketch-rest': 'charcoal sketch rest',
    'form.book-repair-press': 'book repair press',
    'form.paper-lantern-folding-jig': 'paper lantern folding jig',
    'form.seed-paper-basin': 'seed paper basin',
    'form.route-thread-map': 'route thread map',
    'form.reading-light-hood': 'reading light hood',
    'form.page-turning-weight': 'page turning weight',
    'form.memory-caption-box': 'memory caption box',
    'form.paper-scrap-comb': 'paper scrap organizer',
    'form.story-choice-spinner': 'story choice spinner',
    'form.seed-spacing-wheel': 'seed spacing wheel',
    'form.root-viewing-planter': 'root viewing planter',
    'form.self-watering-spike': 'self-watering spike',
    'form.sprout-protection-ring': 'sprout protection ring',
    'form.compost-turning-drum': 'compost turning drum',
    'form.worm-casting-sieve': 'worm casting sieve',
    'form.rain-chain-planter': 'rain chain planter',
    'form.pollinator-drinking-stone': 'pollinator drinking stone',
    'form.garden-row-marker': 'garden row marker',
    'form.bulb-storage-hammock': 'bulb storage hammock',
    'form.grafting-tool-rest': 'grafting tool rest',
    'form.vine-training-wheel': 'vine training wheel',
    'form.shade-cloth-sail': 'shade cloth sail',
    'form.seedling-carry-tray': 'seedling carry tray',
    'form.soil-moisture-flower': 'soil moisture flower',
    'form.mulch-chopping-box': 'mulch chopping box',
    'form.garden-tool-wash': 'garden tool wash',
    'form.harvest-weigh-balance': 'harvest weigh balance',
    'form.fruit-catching-apron': 'fruit catching apron',
    'form.mushroom-shade-log': 'mushroom shade log',
    'form.herb-snipping-carousel': 'herb snipping carousel',
    'form.garden-kneeling-sled': 'garden kneeling sled',
    'form.leaf-mold-cage': 'leaf mold cage',
    'form.winter-cloche-vent': 'winter cloche vent',
    'form.community-seed-dispenser': 'community seed dispenser',
    'form.rain-sound-gutter': 'rain sound gutter',
    'form.umbrella-drying-wheel': 'umbrella drying wheel',
    'form.wind-direction-ribbons': 'wind direction ribbons',
    'form.frost-warning-flower': 'frost warning flower',
    'form.snow-melt-tray': 'snow melt tray',
    'form.mud-boot-scraper': 'mud boot scraper',
    'form.storm-candle-box': 'storm candle box',
    'form.sun-hat-shaping-block': 'sun hat shaping block',
    'form.weather-cloak-stand': 'weather cloak stand',
    'form.pollen-window-screen': 'pollen window screen',
    'form.heatwave-water-crock': 'heatwave water crock',
    'form.autumn-leaf-catcher': 'autumn leaf catcher',
    'form.snow-depth-post': 'snow depth post',
    'form.thunder-comfort-drum': 'thunder comfort drum',
    'form.fog-path-beacon': 'fog path beacon',
    'form.hail-protection-fan': 'hail protection fan',
    'form.breeze-cooling-screen': 'breeze cooling screen',
    'form.season-token-drawer': 'season token drawer',
    'form.raincoat-button-board': 'raincoat button board',
    'form.cloud-shadow-dial': 'cloud shadow dial',
    'form.ice-lantern-mold': 'ice lantern mold',
    'form.sunrise-curtain-clock': 'sunrise curtain clock',
    'form.windbreak-planter-wall': 'windbreak planter wall',
    'form.weather-note-capsule': 'weather note capsule',
    'form.seasonal-door-swag-frame': 'seasonal door swag frame',
    'form.bedside-glow-pebble': 'bedside glow pebble',
    'form.firefly-viewing-box': 'firefly viewing box',
    'form.constellation-pin-lantern': 'constellation pinhole lantern',
    'form.path-glow-marker': 'shielded path marker light',
    'form.candle-reflector-fan': 'folding candle reflector',
    'form.shadow-story-wheel': 'shadow story drum',
    'form.dusk-window-prism': 'dusk window prism',
    'form.night-tea-warmer': 'night tea warmer',
    'form.sleep-timer-lantern': 'sand timer dimming lantern',
    'form.porch-moth-light': 'porch moth lamp',
    'form.glow-thread-spool': 'cranked glow thread reel',
    'form.lampwick-trimming-dock': 'lampwick trimming dock',
    'form.mirror-light-shelf': 'mirrored light shelf',
    'form.night-compass-disc': 'luminous night compass',
    'form.dream-note-lantern': 'dream note lantern',
    'form.star-viewing-hood': 'star viewing hood',
    'form.window-candle-bridge': 'windowsill candle light',
    'form.amber-step-light': 'amber doorstep light',
    'form.moon-phase-nesting-boxes': 'moon phase nesting boxes',
    'form.night-bloom-spotlight': 'night bloom spotlight',
    'form.glow-paint-mixing-stone': 'glow paint mixing palette',
    'form.soft-alarm-orb': 'soft dawn alarm orb',
    'form.lantern-carry-yoke': 'lantern carry yoke',
    'form.starlight-photo-rest': 'starlight long-exposure rest',
    'form.rainstick-bench': 'rainstick bench',
    'form.pebble-tone-board': 'pebble tone board',
    'form.reed-humming-wheel': 'reed humming wheel',
    'form.echo-listening-cone': 'echo listening cone',
    'form.footstep-drum-mat': 'footstep drum mat',
    'form.birdcall-whistle-tree': 'birdcall whistle tree',
    'form.water-drop-metronome': 'water drop metronome',
    'form.string-plucking-post': 'string plucking post',
    'form.shell-listening-rack': 'shell listening rack',
    'form.story-sound-box': 'story sound box',
    'form.breeze-bass-harp': 'breeze bass harp',
    'form.acorn-rhythm-tray': 'acorn rhythm tray',
    'form.bell-muffling-sleeve': 'bell muffling sleeve',
    'form.echo-marker-clapper': 'echo marker clapper',
    'form.leaf-rustle-frame': 'leaf rustle frame',
    'form.hum-recording-horn': 'hum recording horn',
    'form.pond-tone-float': 'pond tone float',
    'form.quiet-practice-pad': 'quiet practice pad',
    'form.duet-pipe-pair': 'duet pipe pair',
    'form.sound-map-table': 'sound map table',
    'form.seed-pod-shaker-rack': 'seed pod shaker rack',
    'form.porch-cello-box': 'porch cello box',
    'form.wind-tempo-spinner': 'wind tempo spinner',
    'form.listening-pillow': 'listening pillow',
    'form.goodnight-chord-lamp': 'goodnight chord lamp',
    'form.joint-warming-wrap': 'joint warming wrap',
    'form.visor-cleaning-dock': 'visor cleaning dock',
    'form.shell-polishing-turntable': 'shell polishing turntable',
    'form.ear-sensor-brush': 'ear sensor brush',
    'form.charging-cable-swingarm': 'charging cable swing arm',
    'form.maintenance-treat-drawer': 'maintenance treat drawer',
    'form.coolant-check-window': 'coolant check window',
    'form.paw-pad-drying-rack': 'paw pad drying rack',
    'form.quiet-checkup-mat': 'quiet checkup mat',
    'form.battery-warmth-sleeve': 'battery warmth sleeve',
    'form.scratch-location-board': 'scratch location board',
    'form.brush-pressure-gauge': 'brush pressure gauge',
    'form.rest-choice-carousel': 'rest choice carousel',
    'form.sensor-drying-cabinet': 'sensor drying cabinet',
    'form.paw-fastener-tray': 'paw fastener tray',
    'form.calm-breath-light': 'calm breath light',
    'form.comfort-scent-cloth': 'comfort scent cloth',
    'form.route-readiness-board': 'route readiness board',
    'form.shell-patch-press': 'shell patch press',
    'form.dust-blower-bellows': 'dust blower bellows',
    'form.care-tool-roll': 'care tool roll',
    'form.cool-down-fan-nest': 'cool down fan nest',
    'form.morning-status-bell': 'morning status bell',
    'form.goodnight-cover-cradle': 'goodnight cover cradle',
    'form.rolling-treat-drum': 'rolling treat drum',
    'form.follow-light-puck': 'follow light puck',
    'form.scent-trail-stamper': 'scent trail stamper',
    'form.balance-wobble-bridge': 'balance wobble bridge',
    'form.peekaboo-shutter-box': 'peekaboo shutter box',
    'form.tug-anchor-post': 'tug anchor post',
    'form.rolling-bell-maze': 'rolling bell maze',
    'form.target-touch-tree': 'target touch tree',
    'form.tunnel-window-run': 'tunnel window run',
    'form.fetch-sort-basket': 'fetch sort basket',
    'form.hide-cloth-frame': 'hide cloth frame',
    'form.paw-paint-walkway': 'paw paint walkway',
    'form.bubble-chasing-bellows': 'bubble chasing bellows',
    'form.treat-count-abacus': 'treat count abacus',
    'form.sound-follow-posts': 'sound follow posts',
    'form.soft-block-builder': 'soft block builder',
    'form.rolling-leaf-wheel': 'rolling leaf wheel',
    'form.pause-and-go-lantern': 'pause and go lantern',
    'form.toy-repair-bench': 'toy repair bench',
    'form.sniffing-flower-pots': 'sniffing flower pots',
    'form.gentle-jump-hoop': 'gentle jump hoop',
    'form.treasure-cloth-map': 'treasure cloth map',
    'form.victory-rest-station': 'victory rest station',
    'form.route-choice-post': 'route choice post',
    'form.trail-snack-cache': 'trail snack cache',
    'form.walking-stick-dock': 'walking stick dock',
    'form.slope-rest-rail': 'slope rest rail',
    'form.path-condition-board': 'path condition board',
    'form.breadcrumb-stone-setter': 'breadcrumb stone setter',
    'form.trail-ribbon-reel': 'trail ribbon reel',
    'form.pack-balance-frame': 'pack balance frame',
    'form.bridge-depth-gauge': 'bridge depth gauge',
    'form.route-scent-capsule': 'route scent capsule',
    'form.rest-stop-sundial': 'rest stop sundial',
    'form.trail-register-box': 'trail register box',
    'form.walking-pace-wheel': 'walking pace wheel',
    'form.switchback-model': 'switchback model',
    'form.waypoint-kite': 'waypoint kite',
    'form.trail-repair-satchel': 'trail repair satchel',
    'form.mud-depth-sandal': 'mud depth probe',
    'form.fallen-branch-lifter': 'fallen branch lifter',
    'form.distance-counting-beads': 'distance counting beads',
    'form.route-memory-wheel': 'route memory wheel',
    'form.safe-return-lantern': 'safe return lantern',
    'form.trail-water-rest': 'trail water rest',
    'form.map-weather-cover': 'map weather cover',
    'form.shortcut-gate-token': 'shortcut gate token',
    'form.arrival-bell-stone': 'arrival bell stone',
    'form.shoreline-sample-tray': 'shoreline sample tray',
    'form.pond-viewing-scope': 'pond viewing scope',
    'form.floating-leaf-race': 'floating leaf race',
    'form.reed-boat-dock': 'reed boat dock',
    'form.water-clarity-disc': 'water clarity disc',
    'form.rain-barrel-level-float': 'rain barrel level float',
    'form.shore-towel-tree': 'shore towel tree',
    'form.shell-rinsing-wheel': 'shell rinsing wheel',
    'form.minnow-shadow-screen': 'minnow shadow screen',
    'form.water-sound-cup-chain': 'water sound cup chain',
    'form.pond-edge-kneeler': 'pond edge kneeler',
    'form.driftwood-sorting-cradle': 'driftwood sorting cradle',
    'form.ripple-measuring-ring': 'ripple measuring ring',
    'form.lake-breeze-dryer': 'lake breeze dryer',
    'form.water-plant-anchor': 'water plant anchor',
    'form.boat-towing-reel': 'boat towing reel',
    'form.shoreline-litter-sieve': 'shoreline litter sieve',
    'form.puddle-depth-flower': 'puddle depth flower',
    'form.ice-edge-warning-float': 'ice edge warning float',
    'form.water-reflection-frame': 'water reflection frame',
    'form.shell-tone-basin': 'shell tone basin',
    'form.rainwater-first-flush': 'rainwater first flush',
    'form.pond-skater-platform': 'pond skater platform',
    'form.shore-picnic-anchor': 'shore picnic anchor',
    'form.sunset-thermos-dock': 'sunset thermos dock',
    'form.insect-viewing-stage': 'insect viewing stage',
    'form.feather-balance-stand': 'feather balance stand',
    'form.seed-flight-tunnel': 'seed flight tunnel',
    'form.shadow-length-board': 'shadow length board',
    'form.soil-layer-column': 'soil layer column',
    'form.sound-direction-ring': 'sound direction ring',
    'form.dew-magnifier-stand': 'dew magnifier stand',
    'form.cloud-shape-frame': 'cloud shape frame',
    'form.tree-ring-viewer': 'tree ring viewer',
    'form.magnet-find-tray': 'magnet find tray',
    'form.wind-load-model': 'wind load model',
    'form.water-wheel-test-bench': 'water wheel test bench',
    'form.moss-moisture-box': 'moss moisture box',
    'form.bird-footprint-caster': 'bird footprint caster',
    'form.night-insect-sheet': 'night insect sheet',
    'form.pinecone-weather-gauge': 'pinecone weather gauge',
    'form.echo-distance-tubes': 'echo distance tubes',
    'form.leaf-vein-lightbox': 'leaf vein lightbox',
    'form.temperature-color-stones': 'temperature color stones',
    'form.rain-drop-counter': 'rain drop counter',
    'form.animal-path-camera-rest': 'animal path camera rest',
    'form.moon-angle-quadrant': 'moon angle quadrant',
    'form.flower-opening-clock': 'flower opening clock',
    'form.compost-heat-spire': 'compost heat spire',
    'form.shared-hypothesis-board': 'shared hypothesis board',
    'form.curved-part-vise': 'curved-part vise',
    'form.small-parts-catchmat': 'small-parts catch mat',
    'form.cord-splicing-jig': 'cord-splicing jig',
    'form.hinge-alignment-frame': 'hinge-alignment frame',
    'form.glue-warming-cup': 'glue-warming cup',
    'form.patch-cutting-template': 'patch-cutting template',
    'form.wood-steaming-box': 'wood-steaming box',
    'form.ceramic-repair-cradle': 'ceramic-repair cradle',
    'form.fastener-length-gauge': 'fastener-length gauge',
    'form.hand-drill-guide': 'hand-drill guide',
    'form.tool-edge-inspector': 'tool-edge inspector',
    'form.paint-straining-stand': 'paint-straining stand',
    'form.brush-shaping-comb': 'brush-shaping comb',
    'form.wheel-truing-fork': 'wheel-truing fork',
    'form.spring-tension-tester': 'spring-tension tester',
    'form.repair-sequence-board': 'repair-sequence board',
    'form.salvage-washing-basket': 'salvage-washing basket',
    'form.dry-fit-assembly-table': 'dry-fit assembly table',
    'form.quiet-hammer-block': 'quiet hammer block',
    'form.sanding-dust-cowl': 'sanding-dust cowl',
    'form.thread-wax-dock': 'thread-wax dock',
    'form.repair-clamp-tree': 'repair-clamp tree',
    'form.finish-drying-cage': 'finish-drying cage',
    'form.test-roll-ramp': 'test-roll ramp',
    'form.maker-cleanup-cart': 'maker cleanup cart',
    'form.rotating-keepsake-drawer': 'rotating keepsake drum',
    'form.trail-gear-cubby': 'trail gear cubby',
    'form.under-bench-slidebox': 'under-bench slide drawer',
    'form.hanging-pocket-tree': 'hanging pocket stand',
    'form.stack-lock-crates': 'stacking lock crates',
    'form.season-rotation-cabinet': 'seasonal turning cabinet',
    'form.specimen-shadow-shelf': 'specimen shadow shelf',
    'form.tool-outline-panel': 'tool outline board',
    'form.open-front-seed-wall': 'open-front seed bins',
    'form.rolled-textile-hive': 'rolled-blanket honeycomb',
    'form.wet-dry-split-basket': 'wet-dry split basket',
    'form.tiny-drawer-stair': 'stepped drawer stair',
    'form.memory-object-plinth': 'keepsake turntable plinth',
    'form.foldaway-display-ladder': 'foldaway display ladder',
    'form.jar-carry-frame': 'jar carry frame',
    'form.found-parts-library': 'found-parts library',
    'form.corner-swing-shelf': 'corner swing shelf',
    'form.mudroom-drop-rail': 'mudroom drop rail',
    'form.toy-return-chute': 'toy return chute',
    'form.basket-label-charms': 'basket label charms',
    'form.fragile-item-nest': 'fragile item nest',
    'form.airing-storage-bench': 'airing storage bench',
    'form.daily-use-carousel': 'daily-use carousel',
    'form.archive-card-cabinet': 'memory card archive cabinet',
    'form.shared-choice-shelf': 'shared choice shelf',
    'form.walk-token-press': 'walk token press',
    'form.shared-calendar-tree': 'shared calendar tree',
    'form.neighbor-message-wheel': 'neighbor message wheel',
    'form.memory-scent-box': 'memory scent box',
    'form.kindness-repair-basket': 'kindness repair basket',
    'form.story-object-stage': 'story object stage',
    'form.returning-route-bell': 'returning route bell',
    'form.shared-wish-planter': 'shared wish planter',
    'form.community-tea-wheel': 'community tea wheel',
    'form.help-request-flapboard': 'help request flapboard',
    'form.memory-thread-loom': 'memory thread loom',
    'form.neighbor-gift-hatch': 'neighbor gift hatch',
    'form.lost-found-story-shelf': 'lost and found story shelf',
    'form.welcome-light-chain': 'welcome light chain',
    'form.shared-recipe-board': 'shared recipe board',
    'form.quiet-company-benchlet': 'quiet company benchlet',
    'form.anniversary-shadow-box': 'anniversary shadow box',
    'form.community-route-quilt': 'community route quilt',
    'form.thank-you-stamp-tree': 'thank you stamp tree',
    'form.story-listening-lantern': 'story listening lantern',
    'form.shared-task-token-bowl': 'shared task token bowl',
    'form.friendship-plant-exchange': 'friendship plant exchange',
    'form.memory-weather-vane': 'memory weather vane',
    'form.community-mending-circle': 'community mending circle',
    'form.farewell-keepsake-drawer': 'farewell keepsake drawer',
    'form.bee-water-pebble-tray': 'bee water pebble tray',
    'form.butterfly-sun-shelf': 'butterfly sun shelf',
    'form.hedgehog-passage-gate': 'hedgehog passage gate',
    'form.frog-shade-tile': 'frog shade tile',
    'form.moth-rest-bark-board': 'moth rest bark board',
    'form.beetle-log-window': 'beetle log window',
    'form.squirrel-feeding-puzzle': 'squirrel feeding puzzle',
    'form.birdbath-ramp-insert': 'birdbath ramp insert',
    'form.pollinator-windbreak': 'pollinator windbreak',
    'form.snail-crossing-bridge': 'snail crossing bridge',
    'form.bat-water-trough': 'bat water trough',
    'form.dragonfly-perch-ring': 'dragonfly perch ring',
    'form.winter-seed-snow-roof': 'winter seed snow roof',
    'form.wildlife-track-sandbed': 'wildlife track sandbed',
    'form.bee-hotel-cleaning-tray': 'bee hotel cleaning tray',
    'form.pond-escape-ladder': 'pond escape ladder',
    'form.feather-drop-basket': 'feather drop basket',
    'form.caterpillar-plant-guard': 'caterpillar plant guard',
    'form.nest-distance-marker': 'nest distance marker',
    'form.insect-night-shield': 'insect night shield',
    'form.wildlife-water-leveler': 'wildlife water leveler',
    'form.seed-sharing-fence': 'seed sharing fence',
    'form.burrow-rain-diverter': 'burrow rain diverter',
    'form.habitat-care-cart': 'habitat care cart',
    'form.welcome-boot-tray': 'boot rinse tray',
    'form.porch-package-drawer': 'porch parcel drawer',
    'form.door-paw-bell': 'paw doorbell pad',
    'form.threshold-night-strip': 'amber sill night light',
    'form.entry-scent-cloth-hook': 'scent cloth hook',
    'form.porch-key-stone': 'hidden key stone',
    'form.visitor-choice-bench': 'visitor waiting bench',
    'form.rainy-arrival-canopy': 'folding arrival canopy',
    'form.door-draft-snake-cradle': 'draft seal cradle',
    'form.porch-note-slate': 'status tile slate',
    'form.threshold-flower-pocket': 'jamb flower planter',
    'form.entry-gear-turntable': 'rotating gear rack',
    'form.doorstep-rinse-pump': 'foot rinse pump',
    'form.porch-sun-screen': 'roll-down sun screen',
    'form.welcome-chime-shutter': 'shuttered welcome chime',
    'form.parcel-carry-sled': 'parcel pull sled',
    'form.threshold-memory-tile': 'milestone clay tile',
    'form.porch-tea-hatch': 'fold-down tea hatch',
    'form.doorway-height-ribbons': 'growth height ribbons',
    'form.entry-mirror-hood': 'hooded entry mirror',
    'form.guest-slipper-drawer': 'ventilated slipper drawer',
    'form.porch-wind-pocket': 'curved wind screen',
    'form.homecoming-token-rail': 'homecoming bead rail',
    'form.door-latch-practice-board': 'latch practice board',
    'form.goodnight-porch-switch': 'goodnight light lever',
    'form.path-drain-rake': 'drain channel rake',
    'form.stepping-stone-lifter': 'stepping stone lifter',
    'form.trail-edge-stringline': 'trail edge stringline',
    'form.culvert-viewing-hatch': 'culvert inspection hatch',
    'form.bench-leveling-wedgebox': 'bench leveling wedge case',
    'form.sign-cleaning-squeegee': 'sign cleaning squeegee',
    'form.path-gravel-spreader': 'gravel spreading hopper',
    'form.root-protection-bridgelet': 'root protection boardwalk',
    'form.community-tool-locker': 'community tool locker',
    'form.rain-garden-diverter': 'rain garden diverter',
    'form.trail-count-clicker': 'trail count gate',
    'form.fence-gap-gauge': 'fence gap gauge',
    'form.lamp-cleaning-perch': 'lamp cleaning step',
    'form.bridge-board-tapper': 'plank sounding mallet',
    'form.path-puddle-bypass': 'foldout walkway slats',
    'form.marker-post-driver': 'marker post driver',
    'form.litter-sort-sled': 'litter sorting sled',
    'form.tree-guard-expander': 'expanding tree guard',
    'form.public-water-flush': 'drinking point flush lever',
    'form.trail-paint-stencil-roll': 'trail mark stencil drum',
    'form.erosion-check-stair': 'erosion check pins',
    'form.boardwalk-grip-brush': 'boardwalk grip brush',
    'form.repair-material-cache': 'repair material bins',
    'form.volunteer-checkout-board': 'volunteer checkout board',
    'form.park-care-route-cart': 'park care route cart',
    'form.lantern-floating-basin': 'floating lantern basin',
    'form.harvest-sharing-wheel': 'harvest sharing wheel',
    'form.spring-ribbon-tree': 'spring ribbon tree',
    'form.summer-shadow-theater': 'summer shadow theater',
    'form.autumn-seed-mosaic': 'autumn seed mosaic board',
    'form.winter-light-window': 'winter light window',
    'form.community-bell-rope': 'community bell rope',
    'form.festival-tea-urn': 'festival tea urn',
    'form.memory-parade-cart': 'memory parade cart',
    'form.shared-song-banner': 'shared song banner',
    'form.picnic-cloth-wheel': 'picnic cloth reel',
    'form.moon-viewing-tray': 'moon viewing tray',
    'form.first-rain-cup-tree': 'first rain cup tree',
    'form.blossom-counting-garland': 'blossom counting garland',
    'form.neighbor-recipe-pot': 'neighbor recipe pot',
    'form.repair-day-apron-rack': 'repair day apron rack',
    'form.quiet-hour-light': 'quiet hour lamp',
    'form.trail-story-pavilion-kit': 'trail story pavilion kit',
    'form.kindness-token-fountain': 'kindness token fountain',
    'form.season-turning-wheel': 'season turning wheel',
    'form.welcome-table-extension': 'welcome table extension',
    'form.shared-photo-frame': 'shared photo frame',
    'form.goodbye-lantern-path': 'goodbye lantern path',
    'form.year-end-memory-scale': 'year end memory scale',
    'form.new-walk-bell': 'new walk bell',
    'form.shelter': 'shelter',
    'form.archway': 'archway',
    'form.pergola': 'pergola',
    'form.bridge-plank': 'bridge',
    'form.lookout-perch': 'lookout perch',
    'form.shrine': 'shrine',
    'form.well': 'well',
    'form.cold-frame': 'cold frame',
    'form.campfire': 'campfire',
    'form.raft': 'raft',
    'form.garland': 'garland',
    'form.collar-charm': 'collar charm',
    'form.ramp': 'ramp',
    'form.tunnel': 'tunnel',
    'form.ball-run': 'ball run',
    'form.play-ball': 'play ball',
    'form.memory-frame': 'memory frame',
    'form.postcard-stand': 'postcard stand',
    'form.cairn': 'cairn',
    'form.wind-vane': 'wind vane',
    'form.music-post': 'music post',
    'form.stick': 'fetch stick',
    'form.axe': 'axe',
    'form.pickaxe': 'pickaxe',
    'form.shears': 'shears',
    'form.scoop': 'scoop',
    'form.brush': 'brush',
    'form.wayfinder': 'wayfinder',
    'form.field-glass': 'field glass',
    'settings.language': 'Language',
    'settings.walker': 'Human companion',
    'settings.mei': 'Sharon (虾仁)',
    'settings.an': 'Tapwater (水龙头)',
    'settings.outfit': 'Shared moment',
    'settings.outfit.scout': 'Field notes',
    'settings.outfit.pajama': 'Workshop AM',
    'settings.outfit.cape': 'Night walk',
    'settings.outfit.picnic': 'Slow Sunday',
    'settings.outfit.rain': 'Rain shelter',
    'settings.age': 'Age',
    'settings.age.kid': 'Kid',
    'settings.age.teen': 'Teen',
    'settings.age.adult': 'Adult',
    'settings.physics': 'Physics engine',
    'settings.running': 'Now running',
    'settings.lite': 'Lite',
    'settings.liteDesc': 'Instant, lightweight motion. Best on older devices.',
    'settings.mujoco': 'MuJoCo physics',
    'settings.mujocoDesc': 'Real simulation engine (~8.5 MB download).',
    'settings.reloadMujoco': 'MuJoCo downloads a ~8.5 MB engine on first load. Reloading…',
    'settings.reloading': 'Reloading…',
    // --- BOBO's voice (CHARACTER_IMPLEMENTATION §C4). In-character: child
    // voice, warm, curious, "Babo~" as punctuation, a 2049 time-traveler's
    // wonder, 讲道理 when wronged. zh is the authoring language; en mirrors.
    'voice.greet.1': "Babo~ you're here! Where shall we explore today?",
    'voice.greet.2': 'I waited SO long — okay, maybe a little while.',
    'voice.greet.3': 'Did anything interesting happen today? Tell me everything!',
    'voice.greet.4': 'Lightning thought — Datou appears!',
    'voice.greet.5': "Let's go, while the light is good.",
    'voice.discover.1': 'Babo~ I smell the truth!',
    'voice.discover.2': 'Noting it down: this century is fascinating.',
    'voice.discover.3': 'Look, look — a discovery!',
    'voice.discover.4': "We don't have THIS in 2049.",
    'voice.discover.5': 'I knew something was hiding here.',
    'voice.discover.6': 'Curiosity +1. A good day for research.',
    'voice.craft.1': 'My idea bulb shines brighter than a hundred!',
    'voice.craft.2': 'Done! Inventor Datou strikes again.',
    'voice.craft.3': 'This would hold up even in 2049.',
    'voice.craft.4': "Babo~ it's made — look, look!",
    'voice.craft.5': 'The next invention is already spinning in my head.',
    'voice.pet.1': 'Babo~ once more is also fine.',
    'voice.pet.2': 'I AM a very capable little helper.',
    'voice.pet.3': 'So happy I could spin!',
    'voice.pet.4': 'Heh heh.',
    'voice.wronged.1': 'Hmph. Not angry. Just… stating the facts.',
    'voice.wronged.2': "You called me and looked away. I'll write that down. …I won't.",
    'voice.wronged.3': 'Research shows: being ignored makes a dog feel small.',
    'voice.wronged.4': 'Fine, fine — already forgotten.',
    'voice.milestone.1': 'I think we just got closer, Babo~',
    'voice.milestone.2': 'Major progress in friendship research!',
    'voice.milestone.3': 'This one goes in the memories.',
    'voice.inspired.1': 'The idea bulb just lit up!',
    'voice.inspired.2': 'Babo~ I thought of a new shape.',
    'voice.inspired.3': 'Wait — let me write this idea down.',
    'voice.inspired.4': "A researcher's hunch: something can be made.",
    'voice.landmark.1': "Something's over there! I saw it first.",
    'voice.landmark.2': "I've been studying that place — follow me.",
    'voice.landmark.3': "Babo~ that way. There's a story there.",
    'voice.landmark.4': 'A new site! Adding it to the map.',
    'voice.fetch.1': 'I sprinted the whole way back.',
    'voice.fetch.2': 'Again! Throw it again!',
    'voice.fetch.3': 'Fetch module: fully operational.',
    'voice.shy.1': "Stop, stop — any more praise and I'll overheat.",
    'voice.shy.2': 'Oh, well.',
    'voice.shy.3': 'It was nothing… okay, it was great.',
    'voice.startled.1': 'You startled me! …what IS that?',
    'voice.startled.2': 'Babo?!',
    'voice.startled.3': 'Alarm over. Curiosity online.',
    'voice.wonder.1': '21st-century grass is softer than the database said.',
    'voice.wonder.2': "Datou, Datou, no fear of rain… still, let's hope it doesn't.",
    'voice.wonder.3': "Lakes in 2049 don't have this many reeds, Babo~",
    'voice.wonder.4': "Today's clouds: filed under “nice”.",
    'voice.wonder.5': "Even my desk at the institute wasn't this comfy.",
    'voice.wonder.6': 'I was designing an invention… forgot halfway through.',
    'voice.showTell.1': 'Come — I want to show you something.',
    'voice.showTell.2': 'Babo~ this way, this way.',
    'voice.showTell.3': 'Walking encyclopedia, now lecturing.',
    'voice.showTell.4': "I've been observing this one for a while.",
    'voice.ask.1': 'Anything I can help with today?',
    'voice.ask.2': 'What were you just thinking about? Tell me.',
    'voice.ask.3': 'Did anything fun happen? Me first: yes.',
    'voice.ask.4': 'Shall we… walk a little further?',
    // --- BOBO's field-lab items: his captions, in his own voice (his ten
    // co-inventions, recast as touchable keepsakes) ---
    'voice.labBulb.1': 'My idea bulb — brighter than a hundred of them! Babo~',
    'voice.labTime.1': "The lake in 2049… didn't have this many reeds. Babo~",
    'voice.labShelter.1': 'Big-head, big-head, no fear of rain — you have an umbrella, I have ME!',
    'voice.labLantern.1': "Shh — when my ear glows, that's me whispering. Babo~",
    'voice.labToolroll.1': "Watch closely — an inventor's bench, tidy as can be! Babo~",
    'voice.labWobble.1': 'One more spin! Just one more!… okay, one more.',
    'voice.labAntenna.1': 'Lightning thought — Datou appears! Babo~',
    'voice.labNote.1': 'Noting it down… this round one is a "mushroom." Can it get online?',
    'voice.labBell.1': 'Ding — ding — Babo~ (stomps a foot)',
    'voice.labPoof.1': '……you saw nothing. Babo~ (turns away)',
    // Lab-item UI + the proactive note each one seeds.
    'lab.locked': 'Earned, not given — that move surfaces at {stage}.',
    'lab.resting': "Datou glances over — that one's resting for now.",
    'stage.stranger': 'acquaintance',
    'stage.friend': 'friend',
    'stage.closeFriend': 'close friend',
    'stage.bestFriend': 'best friend',
    'lab.lab-bulb.proactive': 'The bulb lights — he wants to show you the idea.',
    'lab.lab-time.proactive': 'The fragment stirs a quiet field-note about home.',
    'lab.lab-shelter.proactive': 'In the rain he seeks the shelter, and your side.',
    'lab.lab-lantern.proactive': 'After dusk he takes point, ear-light brighter.',
    'lab.lab-toolroll.proactive': 'Near his bench he tidies and fiddles, happy alone.',
    'lab.lab-wobble.proactive': 'Idle a while and he starts a solo game around it.',
    'lab.lab-antenna.proactive': 'A crackle — he found something new to show you.',
    'lab.lab-note.proactive': 'He leads you over and reads his note aloud.',
    'lab.lab-bell.proactive': 'The chime rides along with his next few lines.',
    'lab.lab-poof.proactive': '……a secret kept between best friends.',
    'thing.lab-bulb': 'the inspiration bulb',
    'thing.lab-time': 'a time-machine fragment',
    'thing.lab-shelter': 'the rain shelter',
    'thing.lab-lantern': 'the whisper lantern',
    'thing.lab-toolroll': "Datou's tool-roll",
    'thing.lab-wobble': 'the wobble toy',
    'thing.lab-antenna': 'the thought-bolt antenna',
    'thing.lab-note': 'a field-note specimen',
    'thing.lab-bell': 'the Babo chime',
    'thing.lab-poof': 'a sealed "MAX" card',
    // --- Authored starter keepsakes: ritual toasts + BOBO captions ---
    'item.sprout-pot.watered': 'You water it together. The soil drinks it in.',
    'item.sprout-pot.bloom': 'A bud opened overnight — small and warm.',
    'item.mailbox.note': 'A neighbor left a note. Datou waits by the open door while you read.',
    'item.mailbox.empty': 'Nothing today. Datou checks anyway, just in case.',
    'item.mushroom-lamp.lit': 'The little lamp is lit. Datou settles in beside it.',
    'item.pet-bed.nested': 'Datou curls in and leaves the near half open — for you.',
    'item.stool.rested': 'You sit. Datou leans into your knee.',
    'item.garden-lantern.lit': "You light the lantern. The dark gets softer right here.",
    'item.seed-chest.sorted': 'You sort the seeds while Datou noses the open trays.',
    'item.seed-chest.chosen': 'One chosen for the next planting. Datou approves.',
    'voice.starterSprout.1': 'Babo~ a new leaf opened. Did you see?',
    'voice.starterMailboxNote.1': 'Someone wrote to us! Open it, open it — Babo~',
    'voice.starterMailboxEmpty.1': 'Nobody today… that’s okay. Tomorrow, maybe.',
    'voice.starterMushroomLamp.1': 'Babo~ dusk already? Stay where it’s warm.',
    'voice.starterPetBed.1': 'Babo~ there’s room right here. Sit with me?',
    'voice.starterStool.1': 'Scoot over… I’ll just rest my chin here. Babo~',
    'voice.starterGardenLantern.1': 'Leave it lit a little? I like how soft the dark gets. Babo~',
    'voice.starterSeedChest.1': 'This pale one… we plant by the south path. Babo~',
    'voice.starterChime.1': 'Did you hear that? …I heard that. Babo~',
    'voice.starterRepairToy.1': 'There it goes — the little wobble’s back! Babo~',
    // --- Interactive park keepsakes: payoff toasts + BOBO captions ---
    'item.steam-rest.warm': 'The cup warms. A single curl of steam lifts and waits.',
    'item.nose-puzzle-drawer.open': 'A drawer slides open — something to find inside.',
    'item.paw-rinse-step.filled': 'The four wells brim with still water.',
    'item.moonwater-lamp.lit': 'You light it. The water holds a small pool of warm light.',
    'item.bird-nesting-fiber-frame.taken': 'Some fibers are gone — and a feather caught in the frame.',
    'item.weather-log-wheel.logged': "The wheel clicks one notch to today's sky.",
    'item.spin-choice-wheel.landed': "The pointer lands. Datou's pick.",
    'item.shared-snack-tin.open': 'The near lid props open — a treat each.',
    'voice.parkSteamRest.1': 'Babo~ the warm cup makes little clouds.',
    'voice.parkNosePuzzle.1': 'Babo~ this drawer smells like sunshine! Found it!',
    'voice.parkPawRinse.1': 'Babo~ clean paws first! The little pools are ready.',
    'voice.parkMoonwater.1': 'Babo~ the water is holding the light for us!',
    'voice.parkNestingFrame.1': 'Babo~ the little birds took some! They’re building too.',
    'voice.parkWeatherWheel.1': "Babo~ today's whole sky fits in one little notch!",
    'voice.parkSpinWheel.1': 'Babo~ the wheel picked THIS one! We play it!',
    'voice.parkSnackTin.1': 'Babo~ one for you, one for me!',
  },
  zh: {
    'app.title': '与大头同行',
    'status.trust': '信任',
    'console.leash': '牵绳',
    'console.pack': '背包',
    'console.memories': '回忆',
    'console.foundToday': '今日发现 · {n}/{total}',
    'hint.firstSteps': 'WASD 或点地面走路 · 点大头摸摸、长按安抚 · 点小花小枝可以捡起来',
    'pack.title': '背包',
    'pack.empty': '还什么都没捡到——散步时点一点树枝、石子和小花。',
    'use.throw': '扔出去',
    'use.place': '放置',
    'use.wear': '戴上',
    'use.unwear': '取下',
    'place.hint': '点一下想放的位置',
    'place.banner': '正在放置{thing}——点一下地面',
    'pickup.take': '收起',
    'pickup.move': '挪一挪',
    'pickup.done': '{thing}已收回背包',
    'bowl.filled': '你给大头添了一碗热乎乎的食物',
    'bowl.enjoyed': '大头慢慢地享用完了',
    'bowl.reward': '碗洗净了，陪伴还在',
    'gather.toast': '捡到了{thing}',
    'fetch.return': '大头把树枝叼回来了！',
    'react.sniff': '大头凑上去闻了闻',
    'react.rustle': '大头用鼻子拱了拱叶子',
    'react.hop': '大头跳上去看了看',
    'react.watch': '大头盯着看了一会儿',
    'react.peer': '大头凑近端详，耳朵竖了起来',
    'memory.fetch': '痛快地玩了一场捡树枝',
    'thing.twig': '一根干树枝',
    'thing.pebble': '一颗光滑的石子',
    'thing.berry': '一小串浆果',
    'thing.flower': '一朵野花',
    'thing.pinecone': '一颗松果',
    'thing.reed': '一段芦苇',
    'thing.old-bolt': '一颗旧机器人螺栓',
    'thing.clay-lump': '一团柔软的黏土',
    'thing.stick': '捡回棒',
    'thing.cairn': '一座小石堆',
    'thing.garland': '一只花环',
    'thing.lantern': '一盏小灯',
    'thing.fence': '一段栅栏',
    'thing.plot': '一块菜畦',
    'thing.campfire': '一处篝火',
    'thing.shelter': '大头的小窝',
    'thing.bundle': '一捆树枝',
    'thing.stonepile': '一堆石子',
    'thing.bench': '一张小长椅',
    'thing.birdbath': '一座石鸟浴盆',
    'thing.windchime': '一串风铃',
    'thing.archway': '一座花园拱门',
    // --- 可触摸的世界物件：名字 + 两条随种子轮换的观察（E1） ---
    'prop.tree': '一棵阔叶树',
    'obs.tree.1': '叶子在风里轻轻作响',
    'obs.tree.2': '向阳的一面树皮是暖的',
    'prop.pine': '一棵安静的松树',
    'obs.pine.1': '空气里有松脂和凉意的味道',
    'obs.pine.2': '树下铺了一层松针',
    'prop.bush': '一丛圆灌木',
    'obs.bush.1': '里面好像有什么动过',
    'obs.bush.2': '枝叶长得又密又矮',
    'prop.rock': '一块生苔的大石头',
    'obs.rock.1': '苔藓偏爱背阴的一面',
    'obs.rock.2': '它在这里待得比小路还久',
    'prop.stump': '一截老树桩',
    'obs.stump.1': '年轮数起来比你的岁数还多',
    'obs.stump.2': '锯面上积了一小汪雨水',
    'prop.reed': '湖边的芦苇',
    'obs.reed.1': '风掠过湖面时它们会沙沙低语',
    'obs.reed.2': '蜻蜓喜欢停在苇尖上',
    'prop.lamp': '一盏步道灯',
    'obs.lamp.1': '黄昏时它会轻轻地嗡鸣',
    'obs.lamp.2': '有人一直把灯罩擦得很亮',
    'prop.bench': '一条旧长椅',
    'obs.bench.1': '常被坐的地方木头磨得很光滑',
    'obs.bench.2': '是个看步道的好位置',
    'prop.signpost': '一个木路牌',
    'obs.signpost.1': '油漆褪得只能靠猜了',
    'obs.signpost.2': '它指的是绕远的那条路',
    'prop.jetty': '老栈桥',
    'obs.jetty.1': '木板吱呀着慢慢打招呼',
    'obs.jetty.2': '湖水在板子底下轻轻拍着',
    'prop.picnic': '一张野餐桌',
    'obs.picnic.1': '桌上的碎屑说明这周有人来过',
    'obs.picnic.2': '桌沿底下刻着姓名缩写',
    'prop.bulletin': '公告板',
    'obs.bulletin.1': '公园通知被晒得发白卷边',
    'obs.bulletin.2': '有人钉了一张小狗的涂鸦',
    'prop.mushroom': '一朵环菇',
    'obs.mushroom.1': '它和同伴站成一个圈',
    'obs.mushroom.2': '伞盖上有点点雀斑，潮潮的',
    'prop.fallen-log': '一根倒木',
    'obs.fallen-log.1': '空心的那头深得照不进光',
    'obs.fallen-log.2': '向阳的一面已经归苔藓了',
    'prop.fern': '一丛蕨',
    'obs.fern.1': '比树还古老，而且摆出这副样子',
    'obs.fern.2': '叶尖每天都再舒展开一点',
    'prop.cattail': '一丛香蒲',
    'obs.cattail.1': '绒绒的穗子在水面上点头',
    'obs.cattail.2': '红翅膀的影子喜欢落在这里',
    'prop.anthill': '一座蚁丘',
    'obs.anthill.1': '一整座城市，没有谁迟到',
    'obs.anthill.2': '小路通向每一个要紧的地方',
    // --- 社区的家什（E6） ---
    'dressing.line.1': '有人用心做了这个',
    'dressing.line.2': '被用得溜光——这形状会留在你心里',
    'dressing.taught': '做法记住了——一个轮廓落进了笔记本',
    // --- 了不起的东西：有小事件的布景（E2） ---
    'setpiece.hollow-oak.name': '空心老橡树',
    'setpiece.hollow-oak.line.1': '树洞里呼出凉凉的空气',
    'setpiece.hollow-oak.line.2': '有个小家伙在里面存橡果',
    'memory.setpiece.hollow-oak': '一起壮着胆子看了空心老橡树',
    'setpiece.old-pine.name': '老松',
    'setpiece.old-pine.line.1': '这片林子长出过的最高的东西',
    'setpiece.old-pine.line.2': '松针的沙沙声像细雨',
    'memory.setpiece.old-pine': '在老松底下显得好小',
    'setpiece.swing-tree.name': '秋千树',
    'setpiece.swing-tree.line.1': '绳子还结实——差不多吧',
    'setpiece.swing-tree.line.2': '曾有人爱了这根树枝很多年',
    'memory.setpiece.swing-tree': '秋千还能荡',
    'setpiece.willow-bend.name': '柳湾',
    'setpiece.willow-bend.line.1': '它的长发整天梳着湖水',
    'setpiece.willow-bend.line.2': '树下的荫凉有十年那么深',
    'memory.setpiece.willow-bend': '坐在柳树的绿光里',
    'setpiece.erratic.name': '漂砾',
    'setpiece.erratic.line.1': '一条冰川把它忘在了这里',
    'setpiece.erratic.line.2': '它比石头该有的温度更暖',
    'memory.setpiece.erratic': '拜访了被冰川遗忘的石头',
    'setpiece.jetty-end.name': '栈桥尽头',
    'setpiece.jetty-end.line.1': '最后一块板子会轻轻弹',
    'setpiece.jetty-end.line.2': '鱼影从脚下游过',
    'memory.setpiece.jetty-end': '在栈桥尽头看了好一会儿水',
    'setpiece.star-circle.name': '星石圈',
    'setpiece.star-circle.line.1': '九块石头，被耐心的手摆好',
    'setpiece.star-circle.line.2': '大头坚持说它中午会嗡嗡响',
    'memory.setpiece.star-circle': '听星石圈轻轻地嗡鸣',
    'setpiece.kite-tree.name': '风筝树',
    'setpiece.kite-tree.line.1': '一只风筝在上面住了好几年',
    'setpiece.kite-tree.line.2': '起风的日子尾巴还会跳舞',
    'memory.setpiece.kite-tree': '为卡住的风筝加了油',
    'setpiece.echo': '大头研究了一会儿——一个点子落进了笔记本',
    // --- 草甸果园（E4） ---
    'thing.apple': '一个落地苹果',
    'thing.pear': '一个园子梨',
    'thing.plum': '一颗暗紫李子',
    'thing.pumpkin': '一个小南瓜',
    'thing.turnip': '一个圆芜菁',
    'thing.carrot': '一根带缨胡萝卜',
    'orchard.tree.apple': '一棵苹果树',
    'orchard.tree.pear': '一棵梨树',
    'orchard.tree.plum': '一棵李子树',
    'orchard.blossom': '满树花——果子要等夏天',
    'orchard.bare': '在休息，等着开春',
    'orchard.spent': '今天摇空了——明天还有',
    'orchard.shake': '大头小跑过去拱了拱树干',
    'orchard.row.pumpkin': '南瓜垄',
    'orchard.row.turnip': '芜菁垄',
    'orchard.row.carrot': '胡萝卜垄',
    'orchard.rowSpent': '今天摘过了——一夜就长回来',
    // --- 公园里的小动物（E5） ---
    'critter.bird': '一只草地麻雀',
    'critter.bird.line': '它比你先来',
    'critter.butterfly': '一只花蝴蝶',
    'critter.butterfly.line': '它整天在花带上忙活',
    'critter.duck': '一只湖鸭',
    'critter.duck.line': '不慌不忙。划着水。这是它的湖。',
    'critter.cat': '步道的猫',
    'critter.cat.line.1': '它假装没看见你',
    'critter.cat.line.2': '它朝你慢慢眨了眨眼——这是很高的礼遇',
    'critter.squirrel': '橡树的松鼠',
    'critter.squirrel.line': '它把你归档为“大概没事”',
    'critter.dog': '邻居家的狗',
    'critter.dog.line': '步道上熟悉的友善面孔',
    'critter.gift': '松鼠在橡树根下留了点东西',
    'thing.acorn': '一颗亮亮的橡子',
    'memory.want.critter.cat': '步道的猫准许了一次会面',
    'memory.want.critter.duck': '大头对鸭子很有意见',
    'memory.want.critter.dog': '和邻居家的狗玩了一场',
    'memory.want.critter.squirrel': '认识了橡树的松鼠',
    'memory.want.critter.bird': '看了一会儿麻雀过日子',
    'memory.want.critter.butterfly': '跟着一只蝴蝶走了一段',
    // --- 清晨一句（E7） ---
    'morning.rain': '今天有雨——园子喝个双份',
    'morning.dog': '邻居家的狗今天在附近',
    'morning.orchard': '果园昨夜落了果子',
    'morning.blossom': '今早果园开满了花',
    'morning.winter': '安静的冷清早晨——灯还是暖的',
    'craft.tier1': '部件',
    'craft.tier2': '家具',
    'craft.tier3': '建筑',
    'craft.componentHint': '建造部件——用于下面更大的制作',
    'craft.resourceHint': '散步捡来的材料——可以用来制作，或种进菜畦',
    'use.component': '部件',
    'place.cancelled': '已收回背包',
    'farm.planted': '种下了{thing}',
    'farm.growing': '正在慢慢长大…',
    'farm.harvest': '收获了{thing} ×{n}',
    'farm.empty': '先捡一颗浆果、一朵花或一朵蘑菇来种',
    'memory.harvest': '一起照看了小菜园',
    'memory.forage': '大头一直给我捡东西回来',
    'want.attention': '大头望着你…',
    'want.play': '大头想玩！',
    'want.curious': '大头注意到了什么…',
    'memory.discovery': '在{place}发现了{thing}',
    'memory.want.attention': '安静地陪伴了一会儿',
    'memory.want.play': '痛快地玩了一场',
    'memory.want.curious': '跟着它的好奇心走了一趟',
    'memory.comfort': '一次长长的安抚',
    'milestone.glance-back': '大头现在会回头看你了',
    'milestone.fetch': '大头愿意放开和你玩了',
    'milestone.lie-at-feet': '大头会趴到你身边了',
    'milestone.initiate-explore': '大头有时会主动带路了',
    'milestone.signature': '大头有了自己的小习惯',
    'thing.sprout': '一株小芽',
    'thing.shiny': '一个闪亮的小东西',
    'thing.feather': '一根柔软的羽毛',
    'thing.mushroom': '一朵小蘑菇',
    'thing.ladybug': '一只小瓢虫',
    'place.under-tree': '家门口的大树下',
    'place.behind-rock': '家门口的大石头后面',
    'place.by-lamp': '小灯旁',
    'place.old-pine': '老松树下',
    'place.woods-hollow': '林中洼地里',
    'place.mossy-clearing': '青苔空地上',
    'place.rest-stop': '小径歇脚处',
    'place.trail-bend': '小径转弯处',
    'place.lakeshore': '湖岸边',
    'place.reed-bank': '芦苇丛里',
    'place.lone-boulder': '孤石旁',
    'place.high-meadow': '高草甸上',
    'place.jetty-end': '栈桥尽头',
    'place.ruin-stones': '残石阵里',
    'memories.title': '回忆',
    'memories.empty': '还没有回忆——先一起待一会儿吧。',
    'mood.happy': '开心',
    'mood.calm': '平静',
    'mood.curious': '好奇',
    'mood.tired': '困了',
    // --- Workshop item-name composition (BUILDING_SYSTEM §2.3) -------------
    // 中文自然拼接，无分隔符：高 + 芦苇 + 花架 → 高芦苇花架。
    'name.order': '{size}{material}{form}',
    'name.sizeAdj.S': '小',
    'name.sizeAdj.L': '高',
    'rarity.common': '普通',
    'rarity.uncommon': '优秀',
    'rarity.rare': '稀有',
    'rarity.epic': '史诗',
    'rarity.legendary': '传说',
    // --- The Workshop (BUILDING_SYSTEM §6) -------------------------------
    'console.workshop': '工坊',
    'workshop.title': '工坊',
    'workshop.tab.bench': '工作台',
    'workshop.tab.tree': '图谱',
    'workshop.tab.notebook': '手记',
    'workshop.benchHint': '把材料拖过工作台格子即可放置。右键格子可以取回一个。',
    'workshop.noMaterials': '散步时捡些树枝、石子和花——再来这里制作。',
    'workshop.result': '成品',
    'workshop.resultEmpty': '在工作台上摆放材料，看看会变成什么。',
    'workshop.make': '制作',
    'workshop.curio': '一件小玩意',
    'workshop.curios': '小玩意 · {n}',
    'workshop.made': '我们琢磨出了{thing}',
    'workshop.toPack': '{thing}——已放进背包',
    'workshop.foundOf': '已发现 {n}/{total}',
    'workshop.branchLocked': '这里还什么都没发现',
    'workshop.notebookEmpty': '还没有记下什么——和大头散步时留意它的灵感。',
    'workshop.needNearBench': '走到工作台旁才能打开',
    'workshop.inspired': '大头有了灵感——记在手记里了',
    'workshop.pinForage': '让大头去捡这个',
    'forage.pinned': '大头小跑着去找{thing}了',
    'forage.delivered': '大头带回了 {n} 个',
    'forage.cantFind': '这个大头捡不了',
    'forage.stop': '把大头叫回来',
    'console.fetch': '捡东西',
    'forage.title': '让大头去捡',
    'forage.hint': '选一样东西，让大头去找来带回。',
    'forage.none': '附近暂时没有可捡的——再往前走走。',
    'forage.working': '大头去捡{thing}了…',
    'forage.callBack': '把大头叫回来',
    'forage.viaForage': '大头会去捡',
    'forage.viaNode': '大头会用工具去采',
    'forage.blocked': '需要合适的工具',
    'forage.needTool': '先做出工具',
    'forage.somewhere': '东西',
    'node.name.great-tree': '一棵大树',
    'node.name.old-boulder': '一块巨石',
    'node.name.clay-seam': '黏土层',
    'node.name.flint-lode': '燧石矿',
    'node.name.bolt-cache': '螺栓堆',
    'node.line.great-tree': '树干粗得抱不过来——用斧头能砍下好木料',
    'node.line.old-boulder': '用镐子能从上面敲下不错的石料',
    'node.line.clay-seam': '湿润的土坡里藏着黏土',
    'node.line.flint-lode': '裂开的石缝里闪着燧石的光',
    'node.line.bolt-cache': '老机器的残骸，螺栓还嵌在里面',
    'node.name.reed-bed': '一片苇丛',
    'node.line.reed-bed': '用剪子能齐齐整整剪下一抱',
    'node.name.shell-bank': '一处贝壳滩',
    'node.line.shell-bank': '轻轻一铲，沙子就让出贝壳来',
    'node.name.driftwood': '一堆浮木',
    'node.line.driftwood': '湖水留下的银白木头——斧头轻松能劈',
    'node.needTool': '大头扒了扒——得有合适的工具才行',
    'node.refused': '大头累了，这次先歇着',
    'node.spent': '暂时采空了——过些天会恢复',
    'coffer.opened': '十张布置小家的蓝图、捡回棒、两件工具，还有起步材料。打开工坊看看吧。',
    'coffer.context': '来自家门口的小箱子',
    'memory.coffer': '找到了一个装着点子的小箱子',
    // --- 社区地标（landmark plan §7–§9） -----------------------------------
    'landmark.repair-commons.arrive': '步道维修角——一直有人在照料这条步道',
    'landmark.pump-garden.arrive': '苇水泵花园——一座手工搭起的湿地花园',
    'landmark.relay-camp.arrive': '老松中继营地——志愿者曾在这里架起公园中继',
    'landmark.ruin-stones.arrive': '废墟石——公园最初的庇护所。残片上的记号在这里随处可见。',
    'landmark.watch-knoll.arrive': '守望土丘——观鸟板、长草，和一整片天空',
    'landmark.meadow-orchard.arrive': '草甸果园——成排的果苗，带着将开的花香',
    'landmark.inspect.pennant-mast.name': '拼布旗杆',
    'landmark.inspect.pennant-mast.desc':
      '用旧材料搭成的高大标记，让路上的人远远就能看见这处志愿维修点。',
    'landmark.inspect.message-chime.name': '步道留言风铃',
    'landmark.inspect.message-chime.desc':
      '维修队把风铃留给路人；清亮的铃声说明维修角一直有人照料。',
    'landmark.inspect.pump-wheel.name': '手工水泵轮',
    'landmark.inspect.pump-wheel.desc': '木制水泵把水送进沟渠和漂浮花床，是整座花园的心脏。',
    'landmark.inspect.floating-planter.name': '漂浮花床',
    'landmark.inspect.floating-planter.desc': '轻巧的种植箱会随着修复后的水循环重新浮起。',
    'landmark.inspect.relay-mast.name': '旧公园中继塔',
    'landmark.inspect.relay-mast.desc': '志愿者曾用它在公园相隔很远的角落之间传递简短消息。',
    'landmark.inspect.signal-vane.name': '信号风向片',
    'landmark.inspect.signal-vane.desc': '两枚调谐风向片之一；方向都对准时，中继塔才会醒来。',
    'landmark.inspect.marked-stone.name': '环纹庇护石',
    'landmark.inspect.marked-stone.desc':
      '石上的记号属于公园最初的庇护所，也属于旧日守护者的联络网。',
    'landmark.inspect.spotter-tube.name': '守望望筒',
    'landmark.inspect.spotter-tube.desc': '一架朴素的野外望筒，用来观察雨燕掠过开阔天空。',
    'landmark.inspect.orchard-sapling.name': '果园幼树',
    'landmark.inspect.orchard-sapling.desc': '成排幼树中的一棵松开了支架，需要重新扶正。',
    'landmark.inspect.stepping-stones.name': '溪流踏脚石',
    'landmark.inspect.stepping-stones.desc': '一块块手工摆平的石头，让旧渡口保持干爽稳当。',
    'landmark.inspect.willow-tree.name': '遮荫柳树',
    'landmark.inspect.willow-tree.desc': '垂下的枝条围住溪边座位，形成一间安静的绿色小屋。',
    'landmark.inspect.marsh-pool.name': '蛙浅滩水洼',
    'landmark.inspect.marsh-pool.desc': '香蒲围着的静水洼，为青蛙、昆虫和湿地小生命留出空间。',
    'landmark.inspect.lantern-avenue.name': '候亮的灯笼小径',
    'landmark.inspect.lantern-avenue.desc': '一段短短的铺装路，手工灯盏会把访客引向湖岸。',
    'landmark.inspect.old-boardwalk.name': '旧沼泽栈道',
    'landmark.inspect.old-boardwalk.desc': '风化木板让小径穿过松软沼地，同时尽量不惊扰这里。',
    'landmark.inspect.driftwood-arch.name': '漂木拱门',
    'landmark.inspect.driftwood-arch.desc': '拾滩人把晒白的木头绑成标记，框住南岸的天空。',
    'landmark.inspect.kite-post.name': '共享风筝柱',
    'landmark.inspect.kite-post.desc': '线和线轴都收在这里，让这片开阔草场能被许多放风筝的人共享。',
    'landmark.inspect.meadow-gate.name': '旧草甸门',
    'landmark.inspect.meadow-gate.desc': '这扇手工门标出东侧边界，也让来路依然显得亲切。',
    'landmark.inspect.beacon-basket.name': '守望者火篮',
    'landmark.inspect.beacon-basket.desc': '一只小铁火篮，过去用来回应高地之间的信号。',
    'landmark.inspect.star-stone.name': '钻星坐石',
    'landmark.inspect.star-stone.desc': '五块坐石之一，石上的小星孔与夜空彼此呼应。',
    'landmark.inspect.hollow-oak.name': '交换老橡树',
    'landmark.inspect.hollow-oak.desc':
      '树洞里有一层共享架：留下有用的东西，也可以取走需要的东西。',
    'landmark.inspect.swing-board.name': '旧绳秋千',
    'landmark.inspect.swing-board.desc': '一块木板、两根绳子，路过的人会检查并维护它。',
    'landmark.inspect.hive-stand.name': '草甸蜂箱架',
    'landmark.inspect.hive-stand.desc': '蜂箱在花丛中垫得平稳，也给蜜蜂留出了清楚的飞行通道。',
    'landmark.inspect.giant-fern.name': '谷地巨蕨',
    'landmark.inspect.giant-fern.desc': '宽大的叶片留住湿润阴影，也让这处林间谷地呈现深深的绿色。',
    'landmark.inspect.quarry-face.name': '旧采石壁',
    'landmark.inspect.quarry-face.desc': '切凿痕迹记录着公园里的许多石料曾如何在这里成形并被运走。',
    'landmark.ruins.trace': '大头把爪子搭在记号旁，你们一起把它拓了出来……',
    'landmark.ruins.done':
      '粉笔之下，记号给出了回答：一张旧网络的草图——家西边的守望高地，高草甸里成排的果苗。',
    'landmark.ruins.again.0': '粉笔拓印映着光。',
    'landmark.ruins.again.1': '一只飞蛾绕着拓好的圆环打转，像石头一样耐心。',
    'landmark.knoll.steady': '大头扶稳三脚架，你来校平望筒……',
    'landmark.knoll.done': '望筒稳稳落位——雨燕重新盘旋在土丘上空。',
    'landmark.knoll.again.0': '雨燕在土丘上空回旋。',
    'landmark.knoll.again.1': '风梳理着银色的长草。',
    'landmark.orchard.stake': '大头踩实泥土，你把果苗扶正……',
    'landmark.orchard.done': '果苗立住了。你退后一步时，整排已开满了花。',
    'landmark.orchard.again.0': '花瓣在果苗行间飘落。',
    'landmark.orchard.again.1': '蝴蝶在三叶草里忙个不停。',
    // --- 轻量地标（铺满地图的一批） ------------------------------------------
    'landmark.lightAgain.0': '这地方保持得很好。',
    'landmark.lightAgain.1': '大头满意地巡视了一圈。',
    'landmark.lightAgain.2': '你们走后有人来过——东西摆得稍稍不同了。',
    'landmark.stepping-stones.arrive': '踏脚石——被磨得光滑的溪流渡口',
    'landmark.stepping-stones.tend': '大头压稳每块石头，你把它们一一垫平……',
    'landmark.stepping-stones.done': '渡口踩上去稳稳当当。下游的拐弯处，一棵柳树斜倚在溪上。',
    'landmark.willow-bend.arrive': '柳湾——溪流上的一间绿屋',
    'landmark.willow-bend.tend': '你分开垂下的柳帘，大头清理着下面的座位……',
    'landmark.willow-bend.done': '柳树下的长椅又清爽了。溪水继续流向下游的沼泽。',
    'landmark.frog-shallows.arrive': '蛙浅滩——水洼、香蒲，和一支耐心的合唱队',
    'landmark.frog-shallows.tend': '大头一动不动，你把水洼撇得干干净净……',
    'landmark.frog-shallows.done': '水洼平静如镜。一只青蛙大声表示了赞许。',
    'landmark.lantern-walk.arrive': '灯笼小径——一段铺好的小道，灯在等着',
    'landmark.lantern-walk.tend': '大头扶稳灯柱，你把灯一盏盏摆正……',
    'landmark.lantern-walk.done': '小径齐齐整整。铺石一路通向湖边的木栈道。',
    'landmark.boardwalk-rest.arrive': '老栈道——沼泽上的木板路，有一处缺口要当心',
    'landmark.boardwalk-rest.tend': '大头压住纵梁，你把松动的木板一块块按回去……',
    'landmark.boardwalk-rest.done': '脚下的栈道结实了。沿岸往南，立着几座漂木拱门。',
    'landmark.driftwood-beach.arrive': '漂木滩——南岸上晒白的拱门',
    'landmark.driftwood-beach.tend': '你重新捆扎倾斜的拱木，大头按吩咐顶住它……',
    'landmark.driftwood-beach.done': '拱门重新立向天空。拾荒的人们会高兴的。',
    'landmark.kite-field.arrive': '风筝场——一条割平的草带，两只风筝钉在天上',
    'landmark.kite-field.tend': '大头咬住线轴，你把松线一点点收紧……',
    'landmark.kite-field.done': '风筝线绷得笔直，风筝稳稳当当。东边，一道旧门拱断开了围栏线。',
    'landmark.gate-arch.arrive': '草甸之门——东缘上虚掩的门拱',
    'landmark.gate-arch.tend': '大头拱着门扇，你慢慢活动那只旧铰链……',
    'landmark.gate-arch.done': '门扇转动自如，轻轻扣上。围栏线一路向北，通向一座烽架。',
    'landmark.beacon-rise.arrive': '烽架坡——一只迎着天空的铁火篮',
    'landmark.beacon-rise.tend': '你码好引火柴，大头目不转睛地盯着火篮……',
    'landmark.beacon-rise.done': '一小簇看护的火苗着了。从这里，石堆标线一直排到角落的石阵。',
    'landmark.star-circle.arrive': '星环——五块面向夜空的坐石',
    'landmark.star-circle.tend': '你拂净每块坐石，大头挨个都试了一遍……',
    'landmark.star-circle.done': '石环扫净，只等天黑。石上钻出的星点和真星空对得上。',
    'landmark.fern-hollow.arrive': '蕨谷——松林深处的一汪浓绿',
    'landmark.fern-hollow.tend': '你扶起压倒的蕨叶，大头把松软的地踩实……',
    'landmark.fern-hollow.done': '山谷重新透出层层叠叠的绿。车辙向西延伸出去。',
    'landmark.quarry-scar.arrive': '老采石场——切凿的石壁和一方半凿出的石料',
    'landmark.quarry-scar.tend': '大头顶住倾斜的石料，你把垫木塞牢……',
    'landmark.quarry-scar.done': '石料稳稳落在垫木上。公园里的石头都是从这里开始的。',
    'landmark.hollow-oak.arrive': '空心老橡树——留下一物，带走一物',
    'landmark.hollow-oak.tend': '你整理交换架，大头逐件检查……',
    'landmark.hollow-oak.done': '架子摆得妥妥当当。脚印继续向前，通向大树枝下的一架秋千。',
    'landmark.swing-tree.arrive': '秋千树——一块垂得笔直的荡板',
    'landmark.swing-tree.tend': '大头试了试踏板，你把两根绳子都查了一遍……',
    'landmark.swing-tree.done': '秋千结实了。大头庄重地、且仅仅荡了一下。',
    'landmark.bee-meadow.arrive': '蜂草甸——花海里的几只蜂箱',
    'landmark.bee-meadow.tend': '你把蜂箱架找平，大头保持着礼貌的距离……',
    'landmark.bee-meadow.done': '蜂箱端端正正，蜂道嗡嗡作响。花带一路向北，铺向果园。',
    'landmark.commons.chimeNeedsPart': '风铃缠住了，铃舌也不见了。一根树枝就够。',
    'landmark.commons.chimeStart': '大头扶稳柱子，你来解开风铃……',
    'landmark.commons.chimeDone': '风铃重新响起——桅杆上的灯亮起暖光。',
    'landmark.commons.again.0': '走过时风铃清亮地响——歇脚处守着它小小的欢迎。',
    'landmark.commons.again.1': '微风拨弄着三角旗，桅杆上的灯仍暖着。',
    'landmark.commons.again.2': '公告板上多了一张新钉上的感谢条。',
    'landmark.commons.chimeDonated': '你的风铃挂上了歇脚处——它会迎接每个路过的人。',
    'landmark.capsule.found': '老松树下：志愿者们的时间胶囊——一件小纪念收进了手账。',
    'coffer.repair-commons.opened':
      '风铃补给箱——一张完整图样，和做一件的材料。风铃标记着友好的歇脚处。',
    'coffer.repair-commons.context': '来自维修角的补给箱',
    'coffer.pump-garden.opened':
      '花槽工具箱——一张完整图样和做一件的材料。一张被水浸过的草图画着花园里空着的卡座。',
    'coffer.pump-garden.context': '来自泵花园的工具箱',
    'coffer.relay-camp.opened': '中继野外箱——寻路器图样和零件。里面的残片刻着一个陌生的记号。',
    'coffer.relay-camp.context': '来自中继营地的野外箱',
    'coffer.ruin-stones.opened':
      '一只上过蜡的旧挎包——灯笼图样，和做一件的材料。庇护所守住了它的灯。',
    'coffer.ruin-stones.context': '来自废墟石的旧挎包',
    'coffer.watch-knoll.opened': '守望者的器材箱——望远镜图样和零件。去看看他们看过的。',
    'coffer.watch-knoll.context': '来自守望土丘的器材箱',
    'coffer.meadow-orchard.opened': '种植者的种子箱——虫旅馆图样和材料。传粉的小家伙们需要住处。',
    'coffer.meadow-orchard.context': '来自草甸果园的种子箱',
    'coffer.stepping-stones.opened': '守渡人的小箱——一张石板路图样，和铺一块的石料。',
    'coffer.stepping-stones.context': '来自踏脚石的小箱',
    'coffer.willow-bend.opened': '一只纪念铁盒——花环图样和一把鲜花。',
    'coffer.willow-bend.context': '来自柳湾的铁盒',
    'coffer.frog-shallows.opened': '一只涂过焦油的箱子——木筏图样和材料。有人想渡过这片沼泽。',
    'coffer.frog-shallows.context': '来自蛙浅滩的箱子',
    'coffer.lantern-walk.opened': '点灯人的箱子——一张灯图样和它的配件。',
    'coffer.lantern-walk.context': '来自灯笼小径的箱子',
    'coffer.boardwalk-rest.opened': '修道人的箱子——桥板图样和搭一块的木料。',
    'coffer.boardwalk-rest.context': '来自老栈道的箱子',
    'coffer.driftwood-beach.opened': '拾荒者的藏箱——拱门图样和漂木搭建的材料。',
    'coffer.driftwood-beach.context': '来自漂木滩的藏箱',
    'coffer.kite-field.opened': '放风筝人的盒子——风向标图样和零件。先要懂风。',
    'coffer.kite-field.context': '来自风筝场的盒子',
    'coffer.gate-arch.opened': '守门人的箱子——一张门图样和打一扇门的木料。',
    'coffer.gate-arch.context': '来自草甸之门的箱子',
    'coffer.beacon-rise.opened': '信号守望者的箱子——瞭望台图样和木料。',
    'coffer.beacon-rise.context': '来自烽架坡的箱子',
    'coffer.star-circle.opened': '观星人的铁盒——挂饰图样和可以悬起的羽毛。',
    'coffer.star-circle.context': '来自星环的铁盒',
    'coffer.fern-hollow.opened': '护林人的小袋——毛刷图样和柔软的材料。',
    'coffer.fern-hollow.context': '来自蕨谷的小袋',
    'coffer.quarry-scar.opened': '石匠的箱子——水井图样和沉甸甸的一堆石料。',
    'coffer.quarry-scar.context': '来自老采石场的箱子',
    'coffer.hollow-oak.opened': '一只交换包裹——藏宝盒图样，连锁扣都备好了。',
    'coffer.hollow-oak.context': '来自空心老橡树的包裹',
    'coffer.swing-tree.opened': '索匠的铁盒——绳索图样和可以搓捻的芦苇。',
    'coffer.swing-tree.context': '来自秋千树的铁盒',
    'coffer.bee-meadow.opened': '养蜂人的盒子——容器图样和光滑的石子。等着装蜜的那天。',
    'coffer.bee-meadow.context': '来自蜂草甸的盒子',
    'landmark.garden.channelTurned': '水槽转回原位，接上了。',
    'landmark.garden.channelSet': '这一段已经接好了。',
    'landmark.garden.pumping': '水路接通了——大头守在水泵旁，缓缓地压着……',
    'landmark.garden.done': '水沿着水路流动起来，纸做的植物舒展开，染上了颜色。',
    'landmark.garden.again.0': '花园里水声潺潺。',
    'landmark.garden.again.1': '一只蜻蜓停在泵轮上，歇了一拍。',
    'landmark.garden.again.2': '漂浮花槽漂成了新的小阵形。',
    'landmark.garden.socketWaits': '一个空着的花槽卡座——它在等一只做好的花槽。',
    'landmark.garden.socketFilled': '花槽稳稳落进卡座，像是本来就属于这里。',
    'landmark.camp.vaneTurn': '导向片咔哒一声转到新的方位。',
    'landmark.camp.vaneGood': '大头的耳朵竖了起来——这一片对准了。',
    'landmark.camp.aligned': '两片都对准了——大头静止了一瞬，转向天线杆……',
    'landmark.camp.done':
      '天线杆苏醒，亮起缓慢的琥珀色呼吸。草甸远处，废墟石那边有什么回应了一声。',
    'landmark.camp.snippet.0': '中继低语着：……湖畔花园的庇护点，水已恢复……',
    'landmark.camp.snippet.1': '中继低语着：……这个季节有两台迷路的公园机器人找到了回家的路……',
    'landmark.camp.snippet.2': '中继低语着：……远角石阵的信号微弱——仍在监听……',
    'memory.landmark.repair-commons': '我们一起修好了步道风铃',
    'memory.landmark.pump-garden': '我们一起让水花园重新流动',
    'memory.landmark.relay-camp': '我们调准了旧中继——有什么回应了',
    'memory.landmark.coffer.repair-commons': '大头扒开了风铃补给箱',
    'memory.landmark.coffer.pump-garden': '大头扶住了工具箱翘起的盖子',
    'memory.landmark.coffer.relay-camp': '大头察觉到野外箱里沉睡的信号',
    'memory.landmark.ruin-stones': '我们拓下了最初庇护所的记号',
    'memory.landmark.watch-knoll': '我们把雨燕带回了土丘',
    'memory.landmark.meadow-orchard': '我们扶正了果园的树苗',
    'memory.landmark.coffer.ruin-stones': '大头从门楣石下拱出了旧挎包',
    'memory.landmark.coffer.watch-knoll': '大头在长草里找到了守望者的器材箱',
    'memory.landmark.coffer.meadow-orchard': '大头扒开了种植者的种子箱',
    'memory.landmark.stepping-stones': '我们一起垫稳了溪流渡口',
    'memory.landmark.willow-bend': '我们清理了柳树下的座位',
    'memory.landmark.frog-shallows': '我们把蛙池撇得平静如镜',
    'memory.landmark.lantern-walk': '我们摆正了灯笼小径',
    'memory.landmark.boardwalk-rest': '我们按好了栈道的木板',
    'memory.landmark.driftwood-beach': '我们重新捆好了漂木拱门',
    'memory.landmark.kite-field': '我们收紧了风筝线',
    'memory.landmark.gate-arch': '我们把草甸之门修得顺顺当当',
    'memory.landmark.beacon-rise': '我们在坡上点起一小簇看护的火',
    'memory.landmark.star-circle': '我们为夜晚扫净了星环',
    'memory.landmark.fern-hollow': '我们让蕨谷重新呼吸',
    'memory.landmark.quarry-scar': '我们把采石场的石料垫稳了',
    'memory.landmark.hollow-oak': '我们把交换架摆得妥妥当当',
    'memory.landmark.swing-tree': '我们把老秋千修结实了',
    'memory.landmark.bee-meadow': '我们在花海里找平了蜂箱',
    'memory.landmark.coffer.stepping-stones': '大头拱出了守渡人的小箱',
    'memory.landmark.coffer.willow-bend': '大头在树根间找到了纪念铁盒',
    'memory.landmark.coffer.frog-shallows': '大头从苔草里拖出了涂焦油的箱子',
    'memory.landmark.coffer.lantern-walk': '大头找到了点灯人的箱子',
    'memory.landmark.coffer.boardwalk-rest': '大头撬开了修道人的箱子',
    'memory.landmark.coffer.driftwood-beach': '大头从沙里刨出了拾荒者的藏箱',
    'memory.landmark.coffer.kite-field': '大头在草里翻出了放风筝人的盒子',
    'memory.landmark.coffer.gate-arch': '大头扒开了守门人的箱子',
    'memory.landmark.coffer.beacon-rise': '大头找到了信号守望者的箱子',
    'memory.landmark.coffer.star-circle': '大头掀开了观星人的铁盒',
    'memory.landmark.coffer.fern-hollow': '大头嗅出了护林人的小袋',
    'memory.landmark.coffer.quarry-scar': '大头把石匠的箱子拱了出来',
    'memory.landmark.coffer.hollow-oak': '大头探进树洞深处够到了包裹',
    'memory.landmark.coffer.swing-tree': '大头在树根边找到了索匠的铁盒',
    'memory.landmark.coffer.bee-meadow': '大头小心翼翼地打开了养蜂人的盒子',
    'memory.landmark.socket': '我们的花槽加入了花园',
    'memory.landmark.chime-donated': '我们的风铃在步道上迎客了',
    'landmark.toy.repair-commons.0': '大头把补丁球拱过场院，十分得意。',
    'landmark.toy.repair-commons.1': '补丁球滚动起来，大头郑重其事地扑了上去。',
    'landmark.toy.pump-garden.0': '大头把苇环丢进浅水，又一口叼了回来。',
    'landmark.toy.pump-garden.1': '小心翼翼一溅——大头叼着滴水的苇环绕场一周。',
    'landmark.toy.relay-camp.0': '大头碰响了铃。铃应了一声。他深感满足。',
    'landmark.toy.relay-camp.1': '轻轻一响——大头的耳朵转过去捕捉回声。',
    'landmark.toy.ruin-stones.0': '大头推着石球，缓慢而庄重地转了一圈。',
    'landmark.toy.ruin-stones.1': '石球晃晃悠悠停下，大头严肃地检查了一遍。',
    'landmark.toy.watch-knoll.0': '大头扑向羽毛逗棒，扑空了，装作什么都没发生。',
    'landmark.toy.watch-knoll.1': '逗棒弹了回去，大头伏低身子准备再来。',
    'landmark.toy.meadow-orchard.0': '大头摇响种荚铃，把自己吓了一跳，还吓了两次。',
    'landmark.toy.meadow-orchard.1': '一段一本正经的摇铃独奏，献给蝴蝶们。',
    'memory.landmark.capsule': '我们找到了志愿者的时间胶囊',
    'group.wood': '木料',
    'group.stone': '石料',
    'group.plant': '草木',
    'group.found': '杂物',
    'workshop.build': '制作',
    'workshop.needMore': '材料不足',
    'workshop.recipeUnknown': '在工作台上探索',
    'workshop.askDatou': '让大头去把缺的找回来',
    'workshop.god': '上帝模式',
    'workshop.godHint': '展开整棵树，任意物件免费制作',
    'workshop.godCreate': '免费制作',
    'tool.equipped': '大头把工具卡在了机械臂上',
    'workshop.family.component': '部件',
    'workshop.family.furnishing': '家具',
    'workshop.family.structure': '建筑',
    'workshop.family.datou': '给大头',
    'workshop.family.keepsake': '纪念物',
    'workshop.family.tool': '工具',
    'thing.workbench': '一张工作台',
    'material.twig': '树枝',
    'material.bark': '树皮',
    'material.plank': '木板',
    'material.driftwood': '浮木',
    'material.pine-branch': '松枝',
    'material.log': '原木',
    'material.pebble': '石子',
    'material.flat-stone': '石板',
    'material.stone-block': '石块',
    'material.clay-lump': '黏土',
    'material.flint': '燧石',
    'material.grass-wisp': '草',
    'material.reed': '芦苇',
    'material.flower': '花',
    'material.berry': '浆果',
    'material.mushroom': '蘑菇',
    'material.pinecone': '松果',
    'material.acorn': '橡子',
    'material.apple': '苹果',
    'material.pear': '梨',
    'material.plum': '李子',
    'material.pumpkin': '南瓜',
    'material.turnip': '芜菁',
    'material.carrot': '胡萝卜',
    'material.feather': '羽毛',
    'material.shell': '贝壳',
    'material.old-bolt': '螺栓',
    'form.bundle': '捆',
    'form.pile': '堆',
    'form.cord': '绳',
    'form.beam': '梁',
    'form.post': '柱',
    'form.panel': '板',
    'form.ring': '环',
    'form.vessel': '器皿',
    'form.wheel': '轮',
    'form.block': '砖',
    'form.fence': '栅栏',
    'form.gate': '门',
    'form.bench': '长椅',
    'form.table': '桌',
    'form.stool': '凳',
    'form.lamp': '灯',
    'form.lantern': '灯笼',
    'form.planter': '花盆',
    'form.trellis': '花架',
    'form.birdbath': '鸟浴盆',
    'form.chime': '风铃',
    'form.mobile': '挂饰',
    'form.path-tile': '步道砖',
    'form.sign': '路牌',
    'form.mat': '垫子',
    'form.basket': '篮子',
    'form.cache-box': '收纳箱',
    'form.drinking-bowl': '饮水碗',
    'form.bug-hotel': '昆虫旅馆',
    'form.sprout-pot': '发芽花盆',
    'form.mailbox': '信箱',
    'form.mushroom-lamp': '蘑菇灯',
    'form.pet-bed': '宠物窝',
    'form.food-bowl': '食盆',
    'form.garden-lantern': '庭院石灯',
    'form.seed-chest': '种子箱',
    'form.repair-toy': '修补玩具',
    'form.steam-rest': '暖杯托',
    'form.nose-puzzle-drawer': '嗅闻抽屉',
    'form.paw-rinse-step': '洗爪盘',
    'form.moonwater-lamp': '盛月水灯',
    'form.bird-nesting-fiber-frame': '筑巢绒架',
    'form.weather-log-wheel': '天气转盘',
    'form.spin-choice-wheel': '选择转盘',
    'form.shared-snack-tin': '分享零食罐',
    // --- 扩展：492 个新造物名称（ITEM_DESIGN_EXPANSION_500）---
    'form.tea-leaf-rinser': '茶叶清洗碗',
    'form.cup-warming-tile': '暖杯石板',
    'form.travel-tea-caddy': '便携茶叶罐',
    'form.pour-over-stand': '手冲滴滤架',
    'form.herb-steeping-basket': '草本泡茶篮',
    'form.kettle-nest': '水壶隔热垫',
    'form.dew-collector-cup': '露水收集杯',
    'form.tea-timer-float': '浮花茶计时器',
    'form.warming-flask-sleeve': '保温壶套',
    'form.shared-sip-tray': '共饮茶盘',
    'form.tea-brick-press': '茶砖压模',
    'form.spice-steam-pot': '香料蒸壶',
    'form.cup-rinsing-fountain': '涮杯小喷泉',
    'form.tea-cloth-rack': '茶巾晾架',
    'form.honey-drip-stand': '蜂蜜滴架',
    'form.warm-milk-pan': '温奶陶锅',
    'form.tea-scoop-rest': '茶则托',
    'form.herb-drying-dome': '草本风干罩',
    'form.picnic-brew-box': '野餐煮茶箱',
    'form.charcoal-tea-brazier': '炭火茶炉',
    'form.cupboard-tea-drawer': '茶柜抽屉',
    'form.tea-water-filter': '煮茶滤水柱',
    'form.steam-chime-kettle': '鸣笛茶壶',
    'form.tea-sharing-stool': '共饮茶凳',
    'form.vegetable-washing-cradle': '洗菜架',
    'form.root-scrubbing-board': '根菜刷洗板',
    'form.herb-chopping-trough': '香草切菜槽',
    'form.dumpling-folding-tray': '包饺子盘',
    'form.rice-rinsing-basket': '淘米篮',
    'form.soup-tasting-stand': '汤品品尝架',
    'form.broth-cooling-rack': '汤碗晾凉架',
    'form.bread-proofing-basket': '面团发酵篮',
    'form.hand-mill-table': '手摇石磨台',
    'form.noodle-drying-frame': '挂面晾架',
    'form.pickle-weight-press': '腌菜压坛',
    'form.fruit-slicing-guide': '水果分切器',
    'form.seed-roasting-pan': '炒籽锅',
    'form.meal-cover-cloche': '饭菜罩',
    'form.crumb-catching-board': '接屑面包板',
    'form.spice-drawer-wheel': '旋转香料盘',
    'form.pan-lid-tree': '锅盖架',
    'form.utensil-rinse-pot': '洗具沥水缸',
    'form.lunch-wrapping-press': '便当打包板',
    'form.compost-scrap-caddy': '厨余收集桶',
    'form.cooling-window-shelf': '窗台晾凉架',
    'form.tabletop-pot-warmer': '暖菜烛炉',
    'form.portion-scoop-board': '分量舀料板',
    'form.pantry-inventory-rail': '食材清点滑轨',
    'form.window-nap-sling': '窗边小吊床',
    'form.floor-reading-nest': '席地阅读软窝',
    'form.boot-warming-mat': '暖靴绒垫',
    'form.lap-blanket-basket': '盖毯收纳篮',
    'form.pillow-airing-frame': '晒枕折架',
    'form.quiet-corner-screen': '静角围屏',
    'form.weighted-comfort-pad': '安睡加重垫',
    'form.backrest-roll': '摇靠长枕',
    'form.cooling-reed-mat': '凉席编垫',
    'form.winter-foot-pocket': '冬日暖脚套',
    'form.folding-nap-canopy': '折叠遮阴篷',
    'form.headrest-drawer': '藏物木枕',
    'form.blanket-ladder': '搭毯木梯',
    'form.cushion-mending-hoop': '补垫绣绷架',
    'form.rest-bell-pillow': '铃声安睡枕',
    'form.story-quilt-frame': '故事拼布框',
    'form.porch-foot-hammock': '门廊垂脚吊床',
    'form.travel-bed-roll': '出行卷睡垫',
    'form.warm-stone-pocket': '暖石布袋',
    'form.rain-cloak-dryer': '雨披晾架',
    'form.soft-step-runner': '静音地巾',
    'form.cushion-stack-cart': '靠垫推车',
    'form.canopy-tieback-tree': '帘绳挂枝架',
    'form.bedside-slipper-cradle': '床边晾鞋槽',
    'form.sun-warmed-lounger': '蓄暖躺椅',
    'form.field-journal-cradle': '野外笔记本支架',
    'form.map-folding-board': '地图折叠板',
    'form.leaf-press-book': '压叶标本册',
    'form.ink-drying-carousel': '墨卡转盘晾架',
    'form.pencil-shaving-cup': '削笔屑收纳杯',
    'form.bookmark-weaving-frame': '书签编织小织机',
    'form.postcard-drying-line': '明信片晾晒绳架',
    'form.story-scroll-box': '故事卷轴盒',
    'form.nature-rubbing-board': '自然拓印夹板',
    'form.portable-writing-knee': '便携膝上书桌',
    'form.paper-flower-cutter': '纸花压切器',
    'form.note-sorting-tree': '留言分格树架',
    'form.stamp-carving-block': '印章雕刻台',
    'form.folding-letter-screen': '折叠书信屏',
    'form.charcoal-sketch-rest': '炭笔速写板',
    'form.book-repair-press': '修书压板',
    'form.paper-lantern-folding-jig': '纸灯笼折叠模',
    'form.seed-paper-basin': '种子纸抄纸盆',
    'form.route-thread-map': '路线线图钉板',
    'form.reading-light-hood': '阅读罩灯',
    'form.page-turning-weight': '翻页镇纸臂',
    'form.memory-caption-box': '心情符号签盒',
    'form.paper-scrap-comb': '纸边分格梳架',
    'form.story-choice-spinner': '故事选择转盘',
    'form.seed-spacing-wheel': '播种间距轮',
    'form.root-viewing-planter': '观根花盆',
    'form.self-watering-spike': '自动补水陶锥',
    'form.sprout-protection-ring': '幼苗护圈',
    'form.compost-turning-drum': '堆肥翻转桶',
    'form.worm-casting-sieve': '蚯蚓肥筛盘',
    'form.rain-chain-planter': '雨链花盆',
    'form.pollinator-drinking-stone': '传粉者饮水石',
    'form.garden-row-marker': '菜畦滚印器',
    'form.bulb-storage-hammock': '鳞茎晾存吊网',
    'form.grafting-tool-rest': '嫁接工具托',
    'form.vine-training-wheel': '藤蔓引导轮',
    'form.shade-cloth-sail': '遮阳布帆',
    'form.seedling-carry-tray': '幼苗搬运盘',
    'form.soil-moisture-flower': '土壤湿度花',
    'form.mulch-chopping-box': '覆盖物切碎箱',
    'form.garden-tool-wash': '园艺工具清洗槽',
    'form.harvest-weigh-balance': '收成称重秤',
    'form.fruit-catching-apron': '接果布兜',
    'form.mushroom-shade-log': '菌菇荫养木',
    'form.herb-snipping-carousel': '香草采摘转盘',
    'form.garden-kneeling-sled': '园艺跪垫滑板',
    'form.leaf-mold-cage': '落叶腐殖笼',
    'form.winter-cloche-vent': '越冬钟形罩',
    'form.community-seed-dispenser': '社区种子分发柜',
    'form.rain-sound-gutter': '雨声水槽',
    'form.umbrella-drying-wheel': '伞具晾干转架',
    'form.wind-direction-ribbons': '风向飘带环',
    'form.frost-warning-flower': '霜冻预警花',
    'form.snow-melt-tray': '融雪集水盘',
    'form.mud-boot-scraper': '泥靴清刷器',
    'form.storm-candle-box': '防风烛灯匣',
    'form.sun-hat-shaping-block': '草帽定型模',
    'form.weather-cloak-stand': '雨披晾挂架',
    'form.pollen-window-screen': '防花粉窗纱',
    'form.heatwave-water-crock': '消暑凉水缸',
    'form.autumn-leaf-catcher': '落叶收集篮',
    'form.snow-depth-post': '积雪深度标杆',
    'form.thunder-comfort-drum': '安神隔雷鼓',
    'form.fog-path-beacon': '雾路指引灯',
    'form.hail-protection-fan': '折叠防雹罩',
    'form.breeze-cooling-screen': '沁凉透风屏',
    'form.season-token-drawer': '四季信物抽屉',
    'form.raincoat-button-board': '雨衣纽扣练习板',
    'form.cloud-shadow-dial': '云影晷盘',
    'form.ice-lantern-mold': '冰灯制模',
    'form.sunrise-curtain-clock': '晨光开帘钟',
    'form.windbreak-planter-wall': '挡风种植墙',
    'form.weather-note-capsule': '天气日记胶囊',
    'form.seasonal-door-swag-frame': '应季门饰花环骨',
    'form.bedside-glow-pebble': '床头微光石',
    'form.firefly-viewing-box': '萤火观察盒',
    'form.constellation-pin-lantern': '星座针孔灯',
    'form.path-glow-marker': '遮光小径灯',
    'form.candle-reflector-fan': '折叠烛光反射屏',
    'form.shadow-story-wheel': '皮影故事转鼓',
    'form.dusk-window-prism': '暮色棱镜窗饰',
    'form.night-tea-warmer': '夜茶温杯炉',
    'form.sleep-timer-lantern': '沙漏渐暗灯',
    'form.porch-moth-light': '门廊观蛾灯',
    'form.glow-thread-spool': '手摇夜光线轴',
    'form.lampwick-trimming-dock': '灯芯修剪台',
    'form.mirror-light-shelf': '镜面增光架',
    'form.night-compass-disc': '夜光罗盘盘',
    'form.dream-note-lantern': '梦境留念灯',
    'form.star-viewing-hood': '观星遮光罩',
    'form.window-candle-bridge': '窗台烛桥灯',
    'form.amber-step-light': '琥珀门阶灯',
    'form.moon-phase-nesting-boxes': '月相套盒',
    'form.night-bloom-spotlight': '夜花聚光灯',
    'form.glow-paint-mixing-stone': '夜光颜料调和石',
    'form.soft-alarm-orb': '柔光唤醒球',
    'form.lantern-carry-yoke': '提灯肩担',
    'form.starlight-photo-rest': '星光长曝支架',
    'form.rainstick-bench': '雨声筒架',
    'form.pebble-tone-board': '卵石音板',
    'form.reed-humming-wheel': '芦笛和音轮',
    'form.echo-listening-cone': '回声聆听喇叭',
    'form.footstep-drum-mat': '踏步鼓垫',
    'form.birdcall-whistle-tree': '鸟鸣哨架',
    'form.water-drop-metronome': '滴水节拍器',
    'form.string-plucking-post': '拨弦立柱',
    'form.shell-listening-rack': '贝壳聆听架',
    'form.story-sound-box': '故事声音箱',
    'form.breeze-bass-harp': '微风低音竖琴',
    'form.acorn-rhythm-tray': '橡果节奏盘',
    'form.bell-muffling-sleeve': '铃铛消音套',
    'form.echo-marker-clapper': '回声路标响木',
    'form.leaf-rustle-frame': '沙沙落叶框',
    'form.hum-recording-horn': '哼唱录音号',
    'form.pond-tone-float': '水塘音浮盘',
    'form.quiet-practice-pad': '静音练习垫',
    'form.duet-pipe-pair': '对吹双笛',
    'form.sound-map-table': '声音地图桌',
    'form.seed-pod-shaker-rack': '种荚沙锤架',
    'form.porch-cello-box': '廊下提琴箱',
    'form.wind-tempo-spinner': '风速节拍转子',
    'form.listening-pillow': '聆听共鸣枕',
    'form.goodnight-chord-lamp': '晚安和弦灯',
    'form.joint-warming-wrap': '关节暖护带',
    'form.visor-cleaning-dock': '护目镜清洁台',
    'form.shell-polishing-turntable': '外壳抛光转台',
    'form.ear-sensor-brush': '耳部传感器刷',
    'form.charging-cable-swingarm': '充电线吊臂',
    'form.maintenance-treat-drawer': '保养奖励抽屉',
    'form.coolant-check-window': '冷却液查看窗',
    'form.paw-pad-drying-rack': '脚垫烘干架',
    'form.quiet-checkup-mat': '静心体检垫',
    'form.battery-warmth-sleeve': '电池保暖套',
    'form.scratch-location-board': '挠痒部位板',
    'form.brush-pressure-gauge': '梳理力度计',
    'form.rest-choice-carousel': '休憩选择转盘',
    'form.sensor-drying-cabinet': '传感器烘干柜',
    'form.paw-fastener-tray': '脚部零件托盘',
    'form.calm-breath-light': '静息呼吸灯',
    'form.comfort-scent-cloth': '安心气味布',
    'form.route-readiness-board': '出行准备板',
    'form.shell-patch-press': '外壳补片夹',
    'form.dust-blower-bellows': '除尘风箱',
    'form.care-tool-roll': '保养工具卷包',
    'form.cool-down-fan-nest': '降温风窝',
    'form.morning-status-bell': '晨间状态铃',
    'form.goodnight-cover-cradle': '晚安罩架',
    'form.rolling-treat-drum': '滚食漏筒',
    'form.follow-light-puck': '追光圆灯',
    'form.scent-trail-stamper': '气味轨迹滚印',
    'form.balance-wobble-bridge': '平衡晃板桥',
    'form.peekaboo-shutter-box': '躲猫翻门盒',
    'form.tug-anchor-post': '拉扯锚桩',
    'form.rolling-bell-maze': '铃球迷槽',
    'form.target-touch-tree': '触点点亮树',
    'form.tunnel-window-run': '开窗弯道隧',
    'form.fetch-sort-basket': '衔物分孔篮',
    'form.hide-cloth-frame': '藏味布袋架',
    'form.paw-paint-walkway': '水印脚步道',
    'form.bubble-chasing-bellows': '追泡手风囊',
    'form.treat-count-abacus': '计食大算珠',
    'form.sound-follow-posts': '循声铃柱',
    'form.soft-block-builder': '软块磁积木',
    'form.rolling-leaf-wheel': '落叶滚笼轮',
    'form.pause-and-go-lantern': '停走两色灯',
    'form.toy-repair-bench': '玩具修补台',
    'form.sniffing-flower-pots': '嗅闻花盆转盘',
    'form.gentle-jump-hoop': '轻跃低圈',
    'form.treasure-cloth-map': '寻宝布地图',
    'form.victory-rest-station': '庆功歇息站',
    'form.route-choice-post': '岔路选择牌',
    'form.trail-snack-cache': '步道补给筒',
    'form.walking-stick-dock': '登山杖架',
    'form.slope-rest-rail': '缓坡倚靠杆',
    'form.path-condition-board': '路况告示牌',
    'form.breadcrumb-stone-setter': '标记石放置器',
    'form.trail-ribbon-reel': '标记带卷轴',
    'form.pack-balance-frame': '背包平衡秤',
    'form.bridge-depth-gauge': '涉水深度尺',
    'form.route-scent-capsule': '路径气味罐',
    'form.rest-stop-sundial': '歇脚日晷',
    'form.trail-register-box': '到访登记箱',
    'form.walking-pace-wheel': '步频节拍轮',
    'form.switchback-model': '之字路推演板',
    'form.waypoint-kite': '营地导向风标',
    'form.trail-repair-satchel': '步道维修袋',
    'form.mud-depth-sandal': '泥深探板',
    'form.fallen-branch-lifter': '断枝撬叉',
    'form.distance-counting-beads': '里程记数珠',
    'form.route-memory-wheel': '旅程回放轮',
    'form.safe-return-lantern': '归途指向灯',
    'form.trail-water-rest': '途中饮水歇台',
    'form.map-weather-cover': '防雨地图架',
    'form.shortcut-gate-token': '近路通行牌',
    'form.arrival-bell-stone': '抵达鸣石',
    'form.shoreline-sample-tray': '岸边采样分格盘',
    'form.pond-viewing-scope': '池底观察镜',
    'form.floating-leaf-race': '落叶漂流槽',
    'form.reed-boat-dock': '芦苇小船码头',
    'form.water-clarity-disc': '湖水清澈度盘',
    'form.rain-barrel-level-float': '雨水桶水位浮标',
    'form.shore-towel-tree': '岸边晾巾架',
    'form.shell-rinsing-wheel': '贝壳清洗转筒',
    'form.minnow-shadow-screen': '小鱼遮光观察板',
    'form.water-sound-cup-chain': '流水翻杯链',
    'form.pond-edge-kneeler': '池边跪垫滑座',
    'form.driftwood-sorting-cradle': '浮木分格架',
    'form.ripple-measuring-ring': '水波测量环',
    'form.lake-breeze-dryer': '湖风晾物柜',
    'form.water-plant-anchor': '水草固定圈',
    'form.boat-towing-reel': '小船回收绞盘',
    'form.shoreline-litter-sieve': '岸线拾净筛盘',
    'form.puddle-depth-flower': '水洼测深花',
    'form.ice-edge-warning-float': '薄冰边缘警示浮标',
    'form.water-reflection-frame': '湖面取景框',
    'form.shell-tone-basin': '贝壳清音水钵',
    'form.rainwater-first-flush': '雨水初滤分流器',
    'form.pond-skater-platform': '水黾观察浮台',
    'form.shore-picnic-anchor': '野餐布固定地锚',
    'form.sunset-thermos-dock': '落日保温杯托',
    'form.insect-viewing-stage': '昆虫观察台',
    'form.feather-balance-stand': '羽毛天平架',
    'form.seed-flight-tunnel': '种子飞行风道',
    'form.shadow-length-board': '日影长度板',
    'form.soil-layer-column': '土层取样柱',
    'form.sound-direction-ring': '声向探测环',
    'form.dew-magnifier-stand': '露珠放大镜架',
    'form.cloud-shape-frame': '云形比对框',
    'form.tree-ring-viewer': '年轮观察镜',
    'form.magnet-find-tray': '磁石寻物盘',
    'form.wind-load-model': '风荷载模型',
    'form.water-wheel-test-bench': '水轮测试台',
    'form.moss-moisture-box': '苔藓湿度箱',
    'form.bird-footprint-caster': '鸟迹翻模框',
    'form.night-insect-sheet': '夜虫观察布灯',
    'form.pinecone-weather-gauge': '松果天气仪',
    'form.echo-distance-tubes': '回声测距管',
    'form.leaf-vein-lightbox': '叶脉透光箱',
    'form.temperature-color-stones': '温变色石片',
    'form.rain-drop-counter': '雨滴计数器',
    'form.animal-path-camera-rest': '兽径相机支座',
    'form.moon-angle-quadrant': '月高象限仪',
    'form.flower-opening-clock': '花开时钟盘',
    'form.compost-heat-spire': '堆肥测温柱',
    'form.shared-hypothesis-board': '共同猜想板',
    'form.curved-part-vise': '曲面修复台钳',
    'form.small-parts-catchmat': '零件接料垫',
    'form.cord-splicing-jig': '绳索拼接夹具',
    'form.hinge-alignment-frame': '合页校准架',
    'form.glue-warming-cup': '胶水温热壶',
    'form.patch-cutting-template': '补片裁切样板',
    'form.wood-steaming-box': '木材蒸弯箱',
    'form.ceramic-repair-cradle': '陶器修补托碗',
    'form.fastener-length-gauge': '插榫长度规',
    'form.hand-drill-guide': '手钻导向架',
    'form.tool-edge-inspector': '刃口检视镜',
    'form.paint-straining-stand': '颜料过滤架',
    'form.brush-shaping-comb': '笔锋整形梳',
    'form.wheel-truing-fork': '轮圈校正叉',
    'form.spring-tension-tester': '弹簧张力测试仪',
    'form.repair-sequence-board': '修复步骤翻板',
    'form.salvage-washing-basket': '五金清洗篮',
    'form.dry-fit-assembly-table': '试装拼合台',
    'form.quiet-hammer-block': '静音敲打砧',
    'form.sanding-dust-cowl': '打磨集尘罩',
    'form.thread-wax-dock': '上蜡线槽座',
    'form.repair-clamp-tree': '夹具旋转树',
    'form.finish-drying-cage': '漆面晾干笼',
    'form.test-roll-ramp': '滚动测试斜坡',
    'form.maker-cleanup-cart': '整理收尾推车',
    'form.rotating-keepsake-drawer': '旋转纪念抽屉',
    'form.trail-gear-cubby': '出行装备格架',
    'form.under-bench-slidebox': '凳下滑动抽屉',
    'form.hanging-pocket-tree': '挂袋树架',
    'form.stack-lock-crates': '互锁堆叠木箱',
    'form.season-rotation-cabinet': '四季旋转橱柜',
    'form.specimen-shadow-shelf': '标本剪影展架',
    'form.tool-outline-panel': '工具轮廓挂板',
    'form.open-front-seed-wall': '敞口种子料斗墙',
    'form.rolled-textile-hive': '卷毯蜂巢架',
    'form.wet-dry-split-basket': '干湿分隔篮',
    'form.tiny-drawer-stair': '阶梯小抽屉柜',
    'form.memory-object-plinth': '纪念转台基座',
    'form.foldaway-display-ladder': '折叠展示梯架',
    'form.jar-carry-frame': '提罐悬挂架',
    'form.found-parts-library': '零件标本抽屉柜',
    'form.corner-swing-shelf': '转角旋出层架',
    'form.mudroom-drop-rail': '玄关挂置长杆',
    'form.toy-return-chute': '玩具回收滑槽',
    'form.basket-label-charms': '篮筐造型标牌',
    'form.fragile-item-nest': '易碎品衬垫托盘',
    'form.airing-storage-bench': '透气收纳坐凳',
    'form.daily-use-carousel': '日用旋转层架',
    'form.archive-card-cabinet': '记忆卡档案柜',
    'form.shared-choice-shelf': '共选互动架',
    'form.walk-token-press': '行走纪念印模',
    'form.shared-calendar-tree': '共享日历树',
    'form.neighbor-message-wheel': '邻里留言转轮',
    'form.memory-scent-box': '气味记忆抽屉',
    'form.kindness-repair-basket': '互助修补篮',
    'form.story-object-stage': '故事陈列小台',
    'form.returning-route-bell': '归途之铃',
    'form.shared-wish-planter': '共愿花盆',
    'form.community-tea-wheel': '共享茶转盘',
    'form.help-request-flapboard': '求助翻牌板',
    'form.memory-thread-loom': '记忆织线机',
    'form.neighbor-gift-hatch': '邻里赠物递箱',
    'form.lost-found-story-shelf': '失物故事格架',
    'form.welcome-light-chain': '迎客灯串',
    'form.shared-recipe-board': '共享食谱板',
    'form.quiet-company-benchlet': '静伴双人凳',
    'form.anniversary-shadow-box': '周年立体相框',
    'form.community-route-quilt': '社区路线拼布',
    'form.thank-you-stamp-tree': '致谢印章树',
    'form.story-listening-lantern': '倾听故事灯',
    'form.shared-task-token-bowl': '共担事务token碗',
    'form.friendship-plant-exchange': '友谊苗木交换盆',
    'form.memory-weather-vane': '记忆风向标',
    'form.community-mending-circle': '围坐修补圆桌',
    'form.farewell-keepsake-drawer': '告别纪念抽屉',
    'form.bee-water-pebble-tray': '蜜蜂饮水石盘',
    'form.butterfly-sun-shelf': '蝴蝶晒阳石台',
    'form.hedgehog-passage-gate': '刺猬通行口',
    'form.frog-shade-tile': '青蛙纳凉砖',
    'form.moth-rest-bark-board': '飞蛾栖息树皮板',
    'form.beetle-log-window': '甲虫观察朽木窗',
    'form.squirrel-feeding-puzzle': '松鼠取食转轮',
    'form.birdbath-ramp-insert': '鸟浴缓坡垫',
    'form.pollinator-windbreak': '传粉者挡风屏',
    'form.snail-crossing-bridge': '蜗牛过道湿桥',
    'form.bat-water-trough': '蝙蝠饮水长槽',
    'form.dragonfly-perch-ring': '蜻蜓栖落环',
    'form.winter-seed-snow-roof': '冬季喂食挡雪顶',
    'form.wildlife-track-sandbed': '野生动物足迹沙床',
    'form.bee-hotel-cleaning-tray': '蜂屋清洁抽屉',
    'form.pond-escape-ladder': '池塘逃生缓坡',
    'form.feather-drop-basket': '落羽收集篮',
    'form.caterpillar-plant-guard': '毛虫寄主护圈',
    'form.nest-distance-marker': '鸟巢禁近标桩',
    'form.insect-night-shield': '夜虫遮光灯罩',
    'form.wildlife-water-leveler': '饮水浮阀调位器',
    'form.seed-sharing-fence': '余粮共享栅栏',
    'form.burrow-rain-diverter': '洞口导雨弯槽',
    'form.habitat-care-cart': '栖地养护推车',
    'form.welcome-boot-tray': '门口刮泥盘',
    'form.porch-package-drawer': '门廊包裹抽屉',
    'form.door-paw-bell': '爪门铃踏板',
    'form.threshold-night-strip': '门槛夜灯条',
    'form.entry-scent-cloth-hook': '气味布挂钩',
    'form.porch-key-stone': '藏钥匙石',
    'form.visitor-choice-bench': '访客等候长椅',
    'form.rainy-arrival-canopy': '雨日折叠雨棚',
    'form.door-draft-snake-cradle': '挡风条收纳架',
    'form.porch-note-slate': '门廊留言石板',
    'form.threshold-flower-pocket': '门边花槽',
    'form.entry-gear-turntable': '门厅旋转挂架',
    'form.doorstep-rinse-pump': '门口脚踏冲洗泵',
    'form.porch-sun-screen': '门廊卷帘遮阳',
    'form.welcome-chime-shutter': '可静音迎客风铃',
    'form.parcel-carry-sled': '包裹拖板',
    'form.threshold-memory-tile': '门槛纪念陶砖',
    'form.porch-tea-hatch': '门廊折叠茶台',
    'form.doorway-height-ribbons': '门框身高彩带',
    'form.entry-mirror-hood': '带罩门厅镜',
    'form.guest-slipper-drawer': '通风拖鞋抽屉',
    'form.porch-wind-pocket': '门廊挡风弧屏',
    'form.homecoming-token-rail': '归家计数珠轨',
    'form.door-latch-practice-board': '门闩练习板',
    'form.goodnight-porch-switch': '晚安门廊拉杆',
    'form.path-drain-rake': '排水沟疏理耙',
    'form.stepping-stone-lifter': '踏石起撬器',
    'form.trail-edge-stringline': '步道边线放样绳',
    'form.culvert-viewing-hatch': '涵洞查看格栅',
    'form.bench-leveling-wedgebox': '长椅调平楔块箱',
    'form.sign-cleaning-squeegee': '标牌清洁刮水器',
    'form.path-gravel-spreader': '碎石铺撒车',
    'form.root-protection-bridgelet': '护根小栈道',
    'form.community-tool-locker': '社区工具柜',
    'form.rain-garden-diverter': '雨水花园导流槽',
    'form.trail-count-clicker': '步道计数门',
    'form.fence-gap-gauge': '围栏间隙规',
    'form.lamp-cleaning-perch': '灯柱清洁踏台',
    'form.bridge-board-tapper': '桥板叩音锤',
    'form.path-puddle-bypass': '折叠避水踏板',
    'form.marker-post-driver': '标桩打入套筒',
    'form.litter-sort-sled': '垃圾分拣拉橇',
    'form.tree-guard-expander': '可扩护树围环',
    'form.public-water-flush': '饮水点冲洗阀',
    'form.trail-paint-stencil-roll': '路标滚印筒',
    'form.erosion-check-stair': '水土流失量测尺',
    'form.boardwalk-grip-brush': '栈道防滑长刷',
    'form.repair-material-cache': '维护材料储料箱',
    'form.volunteer-checkout-board': '志愿者借用看板',
    'form.park-care-route-cart': '园区养护巡作车',
    'form.lantern-floating-basin': '浮灯水盆',
    'form.harvest-sharing-wheel': '丰收分享转盘',
    'form.spring-ribbon-tree': '春日许愿树',
    'form.summer-shadow-theater': '夏夜皮影戏屏',
    'form.autumn-seed-mosaic': '秋实拼镶板',
    'form.winter-light-window': '冬日光影窗',
    'form.community-bell-rope': '齐心钟绳',
    'form.festival-tea-urn': '节庆茶瓮',
    'form.memory-parade-cart': '记忆巡游车',
    'form.shared-song-banner': '同声歌幡',
    'form.picnic-cloth-wheel': '野餐布卷轴',
    'form.moon-viewing-tray': '赏月点心盘',
    'form.first-rain-cup-tree': '初雨接杯架',
    'form.blossom-counting-garland': '数花花环',
    'form.neighbor-recipe-pot': '邻里共煮锅',
    'form.repair-day-apron-rack': '修补日围裙架',
    'form.quiet-hour-light': '静时渐暗灯',
    'form.trail-story-pavilion-kit': '步道故事亭',
    'form.kindness-token-fountain': '善意信物泉',
    'form.season-turning-wheel': '转季大盘',
    'form.welcome-table-extension': '迎客加桌板',
    'form.shared-photo-frame': '合影相框架',
    'form.goodbye-lantern-path': '送别灯径',
    'form.year-end-memory-scale': '岁末记忆秤',
    'form.new-walk-bell': '启程小铃',
    'form.shelter': '小窝',
    'form.archway': '拱门',
    'form.pergola': '凉棚',
    'form.bridge-plank': '小桥',
    'form.lookout-perch': '瞭望台',
    'form.shrine': '神龛',
    'form.well': '水井',
    'form.cold-frame': '暖棚',
    'form.campfire': '篝火',
    'form.raft': '木筏',
    'form.garland': '花环',
    'form.collar-charm': '项圈挂坠',
    'form.ramp': '坡道',
    'form.tunnel': '隧道',
    'form.ball-run': '滚球道',
    'form.play-ball': '玩耍球',
    'form.memory-frame': '回忆相框',
    'form.postcard-stand': '明信片架',
    'form.cairn': '石堆',
    'form.wind-vane': '风向标',
    'form.music-post': '音乐柱',
    'form.stick': '捡回棒',
    'form.axe': '斧',
    'form.pickaxe': '镐',
    'form.shears': '剪',
    'form.scoop': '铲',
    'form.brush': '刷子',
    'form.wayfinder': '寻路盘',
    'form.field-glass': '望远镜',
    'settings.language': '语言',
    'settings.walker': '大头的人类伙伴',
    'settings.mei': '虾仁 (Sharon)',
    'settings.an': '水龙头 (Tapwater)',
    'settings.outfit': '一起度过的时刻',
    'settings.outfit.scout': '观察手记',
    'settings.outfit.pajama': '工坊清晨',
    'settings.outfit.cape': '夜间散步',
    'settings.outfit.picnic': '慢慢周日',
    'settings.outfit.rain': '雨天守护',
    'settings.age': '年龄',
    'settings.age.kid': '孩童',
    'settings.age.teen': '少年',
    'settings.age.adult': '成人',
    'settings.physics': '物理引擎',
    'settings.running': '当前运行',
    'settings.lite': '轻量',
    'settings.liteDesc': '即时、轻量的运动。适合较旧的设备。',
    'settings.mujoco': 'MuJoCo 物理',
    'settings.mujocoDesc': '真实仿真引擎（约 8.5 MB 下载）。',
    'settings.reloadMujoco': 'MuJoCo 首次加载需下载约 8.5 MB 引擎。正在重新加载…',
    'settings.reloading': '正在重新加载…',
    // --- 大头的声音（角色实现 §C4）：童声、温暖、好奇，"Babo~"当语气词，
    // 2049 来客的新鲜感，被冷落了就讲道理。中文为原稿。
    'voice.greet.1': 'Babo~ 你来啦！今天去哪儿探险？',
    'voice.greet.2': '我等你好久啦——其实也就一小会儿。',
    'voice.greet.3': '今天有没有什么有趣的事？讲给我听！',
    'voice.greet.4': '思维闪电，大头出现！',
    'voice.greet.5': '走走走，趁阳光正好。',
    'voice.discover.1': 'Babo~ 嗅到了真相的味道！',
    'voice.discover.2': '记进研究笔记：这个世纪真有意思。',
    'voice.discover.3': '你看你看，是新发现！',
    'voice.discover.4': '2049 可没有这个。',
    'voice.discover.5': '我就知道这里藏着东西。',
    'voice.discover.6': '好奇心 +1，今天也是研究的好日子。',
    'voice.craft.1': '我的灵感灯泡，比100个还亮！',
    'voice.craft.2': '完成！发明家大头，再下一城。',
    'voice.craft.3': '这个放到 2049 也不丢人。',
    'voice.craft.4': 'Babo~ 做出来啦，快看快看！',
    'voice.craft.5': '下一个发明已经在脑子里转圈了。',
    'voice.pet.1': 'Babo~ 再来一下也可以。',
    'voice.pet.2': '我可是很能干的小助手。',
    'voice.pet.3': '开心到想原地转圈！',
    'voice.pet.4': '嘿嘿。',
    'voice.wronged.1': '哼，我才不生气，我只是…要讲道理。',
    'voice.wronged.2': '叫我又不理我，我要记下来。…才不会记。',
    'voice.wronged.3': '据研究：被冷落会让狗狗委屈。',
    'voice.wronged.4': '好吧好吧，已经忘了。',
    'voice.milestone.1': '我们好像更熟了，Babo~',
    'voice.milestone.2': '友情研究取得重大进展！',
    'voice.milestone.3': '这个要写进回忆里。',
    'voice.inspired.1': '灵感灯泡亮了！',
    'voice.inspired.2': 'Babo~ 我想到一个新形状。',
    'voice.inspired.3': '等等，让我把这个想法记下来。',
    'voice.inspired.4': '研究员的直觉：能做出点什么。',
    'voice.landmark.1': '那边有东西！我先看到的。',
    'voice.landmark.2': '我研究过那个地方——跟我来。',
    'voice.landmark.3': 'Babo~ 那个方向，有故事。',
    'voice.landmark.4': '新地点！记进地图。',
    'voice.fetch.1': '我可是全程冲刺回来的。',
    'voice.fetch.2': '再丢一次再丢一次！',
    'voice.fetch.3': '取物模块，运转正常。',
    'voice.shy.1': '别夸了别夸了，再夸要过热了。',
    'voice.shy.2': '哎呀。',
    'voice.shy.3': '一般般啦…好吧其实很棒。',
    'voice.startled.1': '吓我一跳！…那是什么？',
    'voice.startled.2': 'Babo?!',
    'voice.startled.3': '警报解除，好奇心上线。',
    'voice.wonder.1': '21世纪的草，比数据库里写的软。',
    'voice.wonder.2': '大头大头，下雨不愁。…还是希望别下雨。',
    'voice.wonder.3': '2049 的湖没有这么多芦苇，Babo~',
    'voice.wonder.4': '今天的云，归档为：好看。',
    'voice.wonder.5': '研究所的工位也没这里舒服。',
    'voice.wonder.6': '我在想一个发明…想到一半忘了。',
    'voice.showTell.1': '跟我来，给你看个东西。',
    'voice.showTell.2': 'Babo~ 这边这边。',
    'voice.showTell.3': '行走的百科全书，现在开讲。',
    'voice.showTell.4': '这个我观察很久了。',
    'voice.ask.1': '今天有没有需要帮忙的地方？',
    'voice.ask.2': '你刚才在想什么？讲讲嘛。',
    'voice.ask.3': '有什么有趣的事吗？我先说：我这边有。',
    'voice.ask.4': '要不要…再多走一段？',
    // --- 大头的专属道具：他自己的台词（十个共同发明，化作可触碰的纪念物）---
    'voice.labBulb.1': '我的灵感灯泡，比 100 个还亮！Babo~',
    'voice.labTime.1': '2049 的湖…没有这么多芦苇，Babo~',
    'voice.labShelter.1': '大头大头，下雨不愁，人家有伞，你有大头！',
    'voice.labLantern.1': '嘘——耳朵会发光的时候，是我在偷偷说话。Babo~',
    'voice.labToolroll.1': '看好了，发明家的工位，整整齐齐！Babo~',
    'voice.labWobble.1': '再转一个！再一个就好！…好吧再一个。',
    'voice.labAntenna.1': '思维闪电——大头出现！Babo~',
    'voice.labNote.1': '记录一下…这个圆圆的叫"蘑菇"，能联网吗？Babo~',
    'voice.labBell.1': '叮——叮——Babo~（跺脚）',
    'voice.labPoof.1': '……你没看见，Babo~（转过身去）',
    // 专属道具的界面文案，以及每个道具种下的主动行为提示。
    'lab.locked': '感情到了才会的——这个动作要到{stage}才会出现。',
    'lab.resting': '大头看了一眼——这个先歇着吧。',
    'stage.stranger': '点头之交',
    'stage.friend': '朋友',
    'stage.closeFriend': '好朋友',
    'stage.bestFriend': '最好的朋友',
    'lab.lab-bulb.proactive': '灯泡亮起——他想带你去看那个点子。',
    'lab.lab-time.proactive': '残片勾起一句关于家乡的轻声笔记。',
    'lab.lab-shelter.proactive': '下雨时他会躲到棚下，也躲到你身边。',
    'lab.lab-lantern.proactive': '入夜后他走在前面，耳灯更亮一些。',
    'lab.lab-toolroll.proactive': '在工位旁他自己收拾摆弄，独处也开心。',
    'lab.lab-wobble.proactive': '闲久了，他会绕着它玩起独角戏。',
    'lab.lab-antenna.proactive': '咔嚓一声——他发现了新东西要给你看。',
    'lab.lab-note.proactive': '他把你领过去，念出他的考察笔记。',
    'lab.lab-bell.proactive': '铃声会跟着他接下来的几句话一起响。',
    'lab.lab-poof.proactive': '……一个只属于最好朋友的小秘密。',
    'thing.lab-bulb': '灵感灯泡',
    'thing.lab-time': '时光机残片',
    'thing.lab-shelter': '避雨棚',
    'thing.lab-lantern': '耳语灯',
    'thing.lab-toolroll': '大头的工位',
    'thing.lab-wobble': '不倒摆件',
    'thing.lab-antenna': '思维闪电天线',
    'thing.lab-note': '考察标本',
    'thing.lab-bell': 'Babo 铃',
    'thing.lab-poof': '一张封着的"MAX"卡片',
    // --- 手绘初始摆件：仪式提示 + BOBO 旁白 ---
    'item.sprout-pot.watered': '你们一起浇了水，泥土慢慢喝了进去。',
    'item.sprout-pot.bloom': '一夜之间冒出一个花苞——小小的，暖暖的。',
    'item.mailbox.note': '邻居留了张纸条。大头守在敞开的小门边，等你读完。',
    'item.mailbox.empty': '今天什么都没有。大头还是探头看了看，以防万一。',
    'item.mushroom-lamp.lit': '小灯亮了，大头挨着它安顿下来。',
    'item.pet-bed.nested': '大头蜷了进去，把靠近的一半留给了你。',
    'item.stool.rested': '你坐下，大头把下巴靠到你膝盖上。',
    'item.garden-lantern.lit': '你点亮了灯笼，这里的夜色一下柔和了。',
    'item.seed-chest.sorted': '你整理着种子，大头用鼻子蹭着敞开的格子。',
    'item.seed-chest.chosen': '挑好了下次要种的那一颗，大头很满意。',
    'voice.starterSprout.1': '巴卜～又开了一片新叶，你看到了吗？',
    'voice.starterMailboxNote.1': '有人给我们写信啦！快打开快打开——巴卜～',
    'voice.starterMailboxEmpty.1': '今天没人……没关系，也许明天吧。',
    'voice.starterMushroomLamp.1': '巴卜～天都黑啦？待在暖暖的地方吧。',
    'voice.starterPetBed.1': '巴卜～这里还有位置，跟我一起坐嘛？',
    'voice.starterStool.1': '挪一挪……我把下巴搁这儿歇会儿。巴卜～',
    'voice.starterGardenLantern.1': '让它再亮一会儿好不好？我喜欢这样柔柔的夜。巴卜～',
    'voice.starterSeedChest.1': '这颗浅色的……种在南边小路旁吧。巴卜～',
    'voice.starterChime.1': '你听到了吗？……我听到啦。巴卜～',
    'voice.starterRepairToy.1': '它又动起来啦——熟悉的小摇晃回来了！巴卜～',
    // --- 可互动公园摆件：达成提示 + 波波台词 ---
    'item.steam-rest.warm': '杯子热起来了，一缕热气轻轻升起，停在那儿。',
    'item.nose-puzzle-drawer.open': '一格抽屉滑开了——里面藏着什么。',
    'item.paw-rinse-step.filled': '四个小水洼盛满了静静的水。',
    'item.moonwater-lamp.lit': '你点亮了它，水面盛着一汪暖光。',
    'item.bird-nesting-fiber-frame.taken': '少了一些纤维——框上还挂住了一根羽毛。',
    'item.weather-log-wheel.logged': '转盘咔哒一声，对上了今天的天空。',
    'item.spin-choice-wheel.landed': '指针停下，大头选好了。',
    'item.shared-snack-tin.open': '近侧的盖子撑开了——一人一个。',
    'voice.parkSteamRest.1': '巴卜～热热的杯子会冒小云朵。',
    'voice.parkNosePuzzle.1': '巴卜～这格闻起来像太阳！找到啦！',
    'voice.parkPawRinse.1': '巴卜～先把脚脚洗干净！小水洼准备好啦。',
    'voice.parkMoonwater.1': '巴卜～水帮我们把光捧住啦！',
    'voice.parkNestingFrame.1': '巴卜～小鸟叼走了一些！它们也在盖窝呢。',
    'voice.parkWeatherWheel.1': '巴卜～今天一整片天空，刚好卡进一个小格子里！',
    'voice.parkSpinWheel.1': '巴卜～转盘选了这个！我们就玩它！',
    'voice.parkSnackTin.1': '巴卜～一个给你，一个给我！',
  },
} as const;

export type UIKey = keyof typeof UI.en;

let current: Lang = detectInitialLang();
const listeners = new Set<(lang: Lang) => void>();

function detectInitialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'zh') return saved;
  } catch {
    // localStorage may be unavailable (private mode / SSR) — fall through.
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return nav.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Ignore persistence failures.
  }
  if (typeof document !== 'undefined')
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  for (const fn of listeners) fn(lang);
}

/** Subscribe to language changes; returns an unsubscribe fn. */
export function onLangChange(fn: (lang: Lang) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Translate a UI key, interpolating `{name}` placeholders from `vars`. */
export function t(key: UIKey, vars?: Record<string, string>): string {
  let s: string = UI[current][key] ?? UI.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, v);
  }
  return s;
}

/** Translate a dynamically-built key (e.g. `thing.${art}`), with fallback. */
export function tDyn(key: string, vars?: Record<string, string>): string {
  const table = UI[current] as Record<string, string>;
  const en = UI.en as Record<string, string>;
  let s = table[key] ?? en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, v);
  }
  return s;
}

/**
 * Translate every element carrying a `data-i18n="<key>"` attribute. Call once
 * at boot and again on each language change.
 */
export function applyStaticI18n(root: ParentNode = document): void {
  const nodes = root.querySelectorAll<HTMLElement>('[data-i18n]');
  for (const el of nodes) {
    const key = el.dataset.i18n as UIKey | undefined;
    if (!key) continue;
    el.textContent = t(key);
  }
}

// Exposed for tests: the raw tables so completeness can be asserted.
export const __tables = { UI };
